import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import axios from 'axios';
import theme from '../theme';
import { formatNomClient } from '../utils/clientHelpers';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Vrai email vs adresse auto-générée c{n}@noreply.mkw-solutions.be
const isRealEmail = (email) =>
  !!(email && !/^c\d+@noreply\.mkw-solutions\.be$/.test(email));


// Rendu texte formaté en JSX : **gras**, _italique_, __souligné__, \n → <br/>
const parseRichSegments = (line, base) => {
  base = base || { bold: false, italic: false, underline: false };
  const segs = [];
  const re = /(__([^_]+)__)|(\*\*([^*]+)\*\*)|(_([^_]+)_)/g;
  let last = 0, m;
  while ((m = re.exec(line)) !== null) {
    if (m.index > last) segs.push({ text: line.slice(last, m.index), ...base });
    let inner, fmt;
    if (m[1])      { inner = m[2]; fmt = { ...base, underline: true }; }
    else if (m[3]) { inner = m[4]; fmt = { ...base, bold: true }; }
    else if (m[5]) { inner = m[6]; fmt = { ...base, italic: true }; }
    parseRichSegments(inner, fmt).forEach(s => segs.push(s));
    last = m.index + m[0].length;
  }
  if (last < line.length) segs.push({ text: line.slice(last), ...base });
  return segs.filter(s => s.text && s.text.length > 0);
};
const renderRichText = (text) => {
  if (!text) return null;
  return text.split('\n').map((line, li) => (
    <span key={li}>
      {li > 0 && <br />}
      {parseRichSegments(line).map((s, si) => {
        let content = s.text;
        if (s.bold)      content = <strong>{content}</strong>;
        if (s.italic)    content = <em>{content}</em>;
        if (s.underline) content = <u>{content}</u>;
        return <span key={si}>{content}</span>;
      })}
    </span>
  ));
};

// ── Régimes TVA belges ─────────────────────────────────────────────────────
const TVA_REGIMES = {
  standard_21:       { taux: 21, label: 'Standard 21%',                       mention: null },
  renovation_6:      { taux: 6,  label: 'Rénovation résidentielle 6%',         mention: 'Taux réduit de 6% - AR n°20, Rubrique XXXI.' },
  auto_b2b:          { taux: 0,  label: 'Autoliquidation B2B (travaux immo)',  mention: "Autoliquidation : Art. 51, §2, 5° CTVA + Art. 20 AR n°1." },
  intracom_services: { taux: 0,  label: 'Services UE – Reverse Charge',        mention: 'Autoliquidation (Reverse Charge).' },
  exo_intracom_biens:{ taux: 0,  label: 'Livraison intracommunautaire',        mention: 'Exonération TVA — Art. 39bis, 1° CTVA.' },
  export_biens:      { taux: 0,  label: 'Exportation hors UE',                 mention: 'Exonération TVA — Art. 39, §1, 1° CTVA.' },
  services_hors_ue:  { taux: 0,  label: 'Prestation services hors UE',         mention: 'Opération non localisée en Belgique (Art. 21, §2 CTVA).' },
  franchise:         { taux: 0,  label: 'Franchise petites entreprises',       mention: 'Régime franchise petites entreprises (Art. 56bis CTVA).' },
  exo_medical:       { taux: 0,  label: 'Services médicaux/paramédicaux',      mention: 'Exempté de TVA — Art. 44, §1 CTVA.' },
  exo_enseignement:  { taux: 0,  label: 'Enseignement / Culture',              mention: 'Exempté de TVA — Art. 44, §2 CTVA.' },
};
const getRegimesPourTaux = (taux) =>
  Object.entries(TVA_REGIMES)
    .filter(([, v]) => v.taux === parseFloat(taux))
    .map(([k, v]) => ({ code: k, ...v }));

// ─── STATUTS VERROUILLÉS (aucune modification possible) ───────────────────────
const STATUTS_VERROU = ['accepte', 'facture'];
const STATUTS_BLOQUE_SUPPRESSION = ['accepte', 'facture'];

// ════════════════════════════════════════════════════════════════════════════
// InsertLigneBar — Barre d'insertion d'article professionnelle
// Affiche un dropdown compact si sections existent, sinon bouton simple
// Gère aussi l'ajout/renommage de section et l'ajout de sous-total
// ════════════════════════════════════════════════════════════════════════════
const UNITES_OPTIONS = ['', 'h', 'FF', 'stuk', 'm²', 'ml', 'm', 'kg', 'kW', 'kWh', 'jr', 'pcs'];

const InsertLigneBar = ({ devis: d, token, apiUrl, onRefresh, onAddFin }) => {
  const config = { headers: { Authorization: `Bearer ${token}` } };
  const [open, setOpen]       = useState(false);
  const [secTarget, setSecTarget] = useState('fin');  // 'fin' ou ordre de section
  const [loading, setLoading] = useState(false);
  const [addType, setAddType] = useState('article'); // 'article' | 'section' | 'sous_total'

  const sorted = [...(d.LigneDevis || [])].sort((a, b) => (a.ordre || 0) - (b.ordre || 0));
  const sections = sorted.filter(l => (l.type_ligne || 'article') === 'section');
  const hasSections = sections.length > 0;

  const doInsert = async () => {
    setLoading(true);
    try {
      const maxOrdre = sorted.length > 0 ? Math.max(...sorted.map(l => l.ordre || 0)) : 0;

      let insertAfterOrdre = maxOrdre;
      let computedNumeroSection = '';

      if (addType === 'section') {
        // Section : en début, après une section cible, ou en fin
        if (secTarget === 'debut') {
          insertAfterOrdre = 0; // insère avant toutes les lignes
        } else if (secTarget !== 'fin') {
          const secOrdre = parseFloat(secTarget);
          const startIdx = sorted.findIndex(l => l.ordre === secOrdre);
          let lastInSection = startIdx;
          for (let j = startIdx + 1; j < sorted.length; j++) {
            const tl = sorted[j].type_ligne || 'article';
            if (tl === 'section') break;
            lastInSection = j;
          }
          insertAfterOrdre = sorted[lastInSection]?.ordre ?? secOrdre;
        } else {
          insertAfterOrdre = maxOrdre;
        }
        computedNumeroSection = `${sections.length + 1}.`;

      } else if (addType === 'sous_total') {
        // P4: sous-total après la section cible
        const secOrdre = parseFloat(secTarget);
        const startIdx = sorted.findIndex(l => l.ordre === secOrdre);
        let lastInSection = startIdx;
        for (let j = startIdx + 1; j < sorted.length; j++) {
          const tl = sorted[j].type_ligne || 'article';
          if (tl === 'section') break;
          if (tl === 'sous_total') { lastInSection = j - 1; break; }
          lastInSection = j;
        }
        insertAfterOrdre = sorted[lastInSection]?.ordre ?? secOrdre;

      } else {
        // P2: article — bon numéro hiérarchique
        if (secTarget !== 'fin') {
          const secOrdre  = parseFloat(secTarget);
          const secLine   = sorted.find(l => l.ordre === secOrdre);
          const rawSecNum = (secLine?.numero_section || '').replace(/\.+$/, '');
          const secNum    = rawSecNum || String(sections.findIndex(s => s.ordre === secOrdre) + 1);

          const startIdx = sorted.findIndex(l => l.ordre === secOrdre);
          let lastInSection = startIdx;
          let artCountInSec = 0;
          for (let j = startIdx + 1; j < sorted.length; j++) {
            const tl = sorted[j].type_ligne || 'article';
            if (tl === 'section') break;
            if (tl === 'sous_total') { lastInSection = j - 1; break; }
            if (tl === 'article') artCountInSec++;
            lastInSection = j;
          }
          insertAfterOrdre        = sorted[lastInSection]?.ordre ?? secOrdre;
          computedNumeroSection   = `${secNum}.${artCountInSec + 1}`;
        }
      }

      // Décaler les lignes après le point d'insertion
      const toShift = sorted.filter(l => (l.ordre || 0) > insertAfterOrdre);
      for (const l of toShift) {
        await axios.patch(`${apiUrl}/devis/ligne/${l.id_ligne_devis}`,
          { ordre:              (l.ordre || 0) + 1,
            quantite:           parseFloat(l.quantite)      || 0,
            prix_unitaire:      parseFloat(l.prix_unitaire)  || 0,
            tva:                parseFloat(l.tva)            || 0,
            code_tranche:       l.code_tranche       || null,
            type_ligne:         l.type_ligne         || 'article',
            numero_section:     l.numero_section     || null,
            description_personnalisee: l.description_personnalisee || null },
          config
        );
      }

      // Détecter si le devis a des tranches
      const hasT = (d.LigneDevis||[]).some(l => l.code_tranche && l.code_tranche !== 'TF');

      // Créer la nouvelle ligne
      await axios.post(`${apiUrl}/devis/${d.id_devis}/ligne-libre`, {
        ordre:                     insertAfterOrdre + 1,
        description_personnalisee: addType === 'section'   ? `Nouvelle section`
                                 : addType === 'sous_total' ? 'Sous-total'
                                 :                           'Nouvelle prestation',
        quantite:                  addType === 'article' ? 1 : 0,
        prix_unitaire:             0,
        tva:                       addType === 'article' ? (parseFloat(d.taux_tva) || 6) : 0,
        type_ligne:                addType,
        numero_section:            computedNumeroSection,
        code_tranche:              hasT && addType === 'article' ? 'TF' : null,
      }, config);

      await onRefresh();
      setOpen(false);
    } catch(e) {
      console.error('InsertLigne error:', e.response?.data || e.message);
      alert('Erreur insertion : ' + (e.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => { setAddType('article'); setOpen(true); }}
          style={{ padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: '#1e40af', color: 'white', fontWeight: 700, fontSize: 12 }}>
          ＋ Ajouter article
        </button>
        <button onClick={() => { setAddType('section'); setSecTarget('fin'); setOpen(true); }}
          style={{ padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: '#3b82f6', color: 'white', fontWeight: 700, fontSize: 12 }}>
          📂 Ajouter section
        </button>
        <button onClick={() => {
          setAddType('sous_total');
          // Par défaut: première section existante (pas 'fin')
          const firstSec = sorted.find(l => (l.type_ligne || 'article') === 'section');
          setSecTarget(firstSec ? String(firstSec.ordre) : 'fin');
          setOpen(true);
        }}
          style={{ padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: '#7c3aed', color: 'white', fontWeight: 700, fontSize: 12 }}>
          Σ Ajouter sous-total
        </button>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 10, background: '#f8fafc', border: '1px solid #e2e8f0',
      borderRadius: 10, padding: '12px 16px' }}>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: '#1e293b' }}>
        {addType === 'article'    && '＋ Insérer un article'}
        {addType === 'section'    && `📂 Ajouter une section${secTarget === 'debut' ? ' (en début)' : secTarget !== 'fin' ? ' (après section choisie)' : ' (en fin)'}`}
        {addType === 'sous_total' && 'Σ Insérer un sous-total'}
      </div>

      {addType === 'section' && hasSections && (
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>
            Position :
          </label>
          <select value={secTarget} onChange={e => setSecTarget(e.target.value)}
            style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e2e8f0',
              fontSize: 13, outline: 'none', width: '100%', maxWidth: 360 }}>
            {sections.map(sec => (
              <option key={sec.ordre} value={sec.ordre}>
                Après section {sec.numero_section || '#'} — {(sec.description_personnalisee || '').substring(0, 40)}
              </option>
            ))}
            <option value="debut">En début de devis</option>
            <option value="fin">En fin de devis</option>
          </select>
        </div>
      )}

      {addType === 'article' && hasSections && (
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>
            Position :
          </label>
          <select value={secTarget} onChange={e => setSecTarget(e.target.value)}
            style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e2e8f0',
              fontSize: 13, outline: 'none', width: '100%', maxWidth: 360 }}>
            {sections.map(sec => (
              <option key={sec.ordre} value={sec.ordre}>
                Dans section {sec.numero_section || '#'} — {(sec.description_personnalisee || '').substring(0, 40)}
              </option>
            ))}
            <option value="fin">En fin de devis (hors section)</option>
          </select>
        </div>
      )}

      {addType === 'sous_total' && hasSections && (
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>
            Après la section :
          </label>
          <select value={secTarget} onChange={e => setSecTarget(e.target.value)}
            style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e2e8f0',
              fontSize: 13, outline: 'none', width: '100%', maxWidth: 360 }}>
            {sections.map(sec => (
              <option key={sec.ordre} value={sec.ordre}>
                Section {sec.numero_section || '#'} — {(sec.description_personnalisee || '').substring(0, 40)}
              </option>
            ))}
          </select>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={doInsert} disabled={loading}
          style={{ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: loading ? '#94a3b8' : '#1e40af', color: 'white', fontWeight: 700, fontSize: 13 }}>
          {loading ? '⏳…' : 'Confirmer'}
        </button>
        <button onClick={() => setOpen(false)}
          style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0',
            cursor: 'pointer', background: 'white', fontWeight: 600, fontSize: 13, color: '#64748b' }}>
          Annuler
        </button>
      </div>
    </div>
  );
};

const Devis = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();

  const [devis, setDevis]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [expandedDevis, setExpandedDevis] = useState(null);
  const setExpandedId = (id) => setExpandedDevis(id);
  // Édition in-line de l'échéancier sur un devis existant (admin)
  const [editEchDevis,  setEditEchDevis]  = useState(null); // id_devis en cours d'édition
  const [editEchPaliers,setEditEchPaliers]= useState([]);
  const [editEchLoading,setEditEchLoading]= useState(false);
  const [editingLigne, setEditingLigne]   = useState(null);
  const [search,       setSearch]         = useState('');
  const [filtreStatut, setFiltreStatut]   = useState('tous');

  // Modals admin
  const [showRemiseModal, setShowRemiseModal] = useState(null);
  const [remiseData, setRemiseData]           = useState({ pourcentage: 0, raison: '' });
  const [showTVAModal, setShowTVAModal]       = useState(null);
  const [tvaValue, setTVAValue]               = useState(21);
  const [tvaRegimes, setTvaRegimes]           = useState({});
  const [pendingRefs, setPendingRefs]         = useState(null); // refs après déblocage

  // Modal délai paiement facture
  const [showFactureModal, setShowFactureModal] = useState(null); // { id, numero, label }
  const [delaiJours,       setDelaiJours]       = useState('7');
  const [dateLibreFacture, setDateLibreFacture] = useState('');

  // Modal création devis (admin)
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [clients, setClients]                 = useState([]);
  const [createMode, setCreateMode]           = useState('existant'); // 'existant' | 'nouveau'
  const [duplicationSourceId, setDuplicationSourceId] = useState(null); // id_devis source si "Dupliquer"
  const [visibiliteLoading, setVisibiliteLoading] = useState(null); // id_devis en cours de bascule

  // Modal d'envoi email personnalisé
  const [showEnvoiModal,   setShowEnvoiModal]   = useState(null); // id_devis ou null
  const [envoiDevisData,   setEnvoiDevisData]   = useState(null); // objet devis complet
  const [envoiForm,        setEnvoiForm]        = useState({ sujet: '', message: '', inclure_cgv: true });
  const [envoiLoading,     setEnvoiLoading]     = useState(false);

  // Modal confirmation acceptation client (note légale + horodatage en temps réel)
  const [showAcceptModal,  setShowAcceptModal]  = useState(null); // objet devis ou null
  const [acceptCGV,        setAcceptCGV]        = useState(false);

  // ── Modal avenant ────────────────────────────────────────────────────────────
  const [showAvenantModal,  setShowAvenantModal]  = useState(null); // devis parent obj
  const [avenantNotes,      setAvenantNotes]      = useState('');
  const [editingNotesId,  setEditingNotesId]  = useState(null); // id_devis en cours d'edition notes
  const [editingNotesTmp, setEditingNotesTmp] = useState({ notes: '', notes_pdf: '' });
  const [savingNotes,     setSavingNotes]     = useState(false);
  const [avenantUseEch,     setAvenantUseEch]     = useState(false);
  const [avenantPaliers,    setAvenantPaliers]    = useState([]);
  const [avenantLoading,    setAvenantLoading]    = useState(false);
  const [acceptLoading,    setAcceptLoading]    = useState(false);
  const [createForm, setCreateForm] = useState({
    // Sélection client existant
    id_client: '',
    // Création nouveau client
    nom: '', prenom: '', email: '', telephone: '',
    adresse: '', ville: '', code_postal: '', pays: 'Belgique',
    numero_tva: '', type_client: 'particulier', nom_societe: '',
    // Devis
    taux_tva: 6, notes: '', notes_pdf: '',
    // Adresse du chantier (lieu de la prestation)
    adresse_chantier: '', ville_chantier: '', cp_chantier: ''
  });

  // null = facture unique (bouton "Convertir en facture")
  // Tableau = paliers configurés [{ num, label, pct }]
  const [useEcheancier, setUseEcheancier]     = useState(false);
  const [echPaliers,    setEchPaliers]        = useState([
    { num: 1, label: 'Acompte',    pct: 40 },
    { num: 2, label: 'Avancement', pct: 50 },
    { num: 3, label: 'Solde',      pct: 10 },
  ]);

  // Templates prédéfinis pour l'échéancier
  const ECH_TEMPLATES = {
    1: [{ num: 1, label: 'Facture complète', pct: 100 }],
    2: [{ num: 1, label: 'Acompte', pct: 50 }, { num: 2, label: 'Solde', pct: 50 }],
    3: [{ num: 1, label: 'Acompte', pct: 40 }, { num: 2, label: 'Avancement', pct: 50 }, { num: 3, label: 'Solde', pct: 10 }],
    4: [{ num: 1, label: 'Acompte', pct: 30 }, { num: 2, label: 'Avancement 1', pct: 30 }, { num: 3, label: 'Avancement 2', pct: 30 }, { num: 4, label: 'Solde', pct: 10 }],
    5: [{ num: 1, label: 'Acompte', pct: 20 }, { num: 2, label: 'Avancement 1', pct: 20 }, { num: 3, label: 'Avancement 2', pct: 20 }, { num: 4, label: 'Avancement 3', pct: 20 }, { num: 5, label: 'Solde', pct: 20 }],
  };

  const echSomme = echPaliers.reduce((s, p) => s + (parseFloat(p.pct) || 0), 0);
  const echValide = Math.abs(echSomme - 100) < 0.01;

  const updateEchPalier = (idx, field, value) => {
    setEchPaliers(prev => prev.map((p, i) => i === idx ? { ...p, [field]: field === 'pct' ? parseFloat(value) || 0 : value } : p));
  };

  const setNbPaliers = (n) => {
    setEchPaliers(ECH_TEMPLATES[n] || ECH_TEMPLATES[3]);
  };
  const setNbPaliersAvenant = (n) => {
    setAvenantPaliers(ECH_TEMPLATES[n] || ECH_TEMPLATES[3]);
  };
  const updateAvenantPalier = (idx, field, value) => {
    setAvenantPaliers(prev => prev.map((p, i) => i === idx ? { ...p, [field]: field === 'pct' ? parseFloat(value) || 0 : value } : p));
  };
  const [createLignes, setCreateLignes] = useState([
    { description_personnalisee: '', quantite: 1, prix_unitaire: 0, tva: 6, unite: '', type_ligne: 'article', numero_section: '' }
  ]);
  const [createLoading, setCreateLoading] = useState(false);
  const [tempPasswordInfo, setTempPasswordInfo] = useState(null); // affiché après création client+devis

  const user    = JSON.parse(localStorage.getItem('user') || '{}');
  const token   = localStorage.getItem('token');
  const config  = { headers: { Authorization: `Bearer ${token}` } };
  const isAdmin = user.role === 'admin';

  useEffect(() => {
    fetchDevis();
    if (isAdmin) fetchClients();
  }, []);

  // Auto-expand : priorité au param ?valider=ID, sinon premier devis 'envoye'
  useEffect(() => {
    if (devis.length > 0 && expandedDevis === null) {
      const validerParam = searchParams.get('valider');
      if (validerParam) {
        const id = parseInt(validerParam, 10);
        const found = devis.find(d => d.id_devis === id);
        if (found) { setExpandedDevis(id); return; }
      }
      if (!isAdmin) {
        const aValider = devis.find(d => d.statut === 'envoye');
        if (aValider) setExpandedDevis(aValider.id_devis);
      }
    }
  }, [devis]); // eslint-disable-line

  const trierLignes = (l) => l ? [...l].sort((a, b) => (a.ordre || 0) - (b.ordre || 0)) : [];

  // Sauvegarde de l'échéancier sur un devis existant (admin)
  const sauvegarderEcheancierDevis = async (id_devis) => {
    const somme = editEchPaliers.reduce((s, p) => s + (parseFloat(p.pct) || 0), 0);
    if (Math.abs(somme - 100) > 0.01) return alert('La somme des paliers doit être 100%.');
    setEditEchLoading(true);
    try {
      await axios.patch(`${API_URL}/devis/${id_devis}/config-echeancier`, { config_echeancier: editEchPaliers }, config);
      setEditEchDevis(null);
      fetchDevis();
    } catch (e) {
      alert(e.response?.data?.message || 'Erreur lors de la sauvegarde.');
    } finally { setEditEchLoading(false); }
  };
  const ouvrirEditEcheancier = (d) => {
    const paliers = (d.config_echeancier && Array.isArray(d.config_echeancier) && d.config_echeancier.length > 0)
      ? d.config_echeancier
      : ECH_TEMPLATES[1]; // défaut : 1 palier 100%
    setEditEchPaliers(paliers);
    setEditEchDevis(d.id_devis);
  };

  const fetchDevis = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/devis`, config);
      const data = (res.data.data || []).map(d => ({
        ...d, LigneDevis: trierLignes(d.LigneDevis)
      }));
      setDevis(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API_URL}/clients`, config);
      setClients(res.data.data || []);
    } catch (err) { console.error(err); }
  };

  // ─── VERROU : helper ────────────────────────────────────────────────────────
  const estVerrouille = (d) => STATUTS_VERROU.includes(d.statut);

  // ─── CRÉATION DEVIS (admin) ──────────────────────────────────────────────────

  const handleCreateDevis = async (e) => {
    e.preventDefault();
    // Lignes valides: articles avec description non vide (sections et sous-totaux toujours inclus)
    // Les lignes avec prix=0 sont CONSERVÉES mais déclenchent un avertissement
    const lignesValides = createLignes.filter(l => {
      // Sections et sous-totaux toujours inclus même si description vide
      if (l.type_ligne === 'section' || l.type_ligne === 'sous_total') return true;
      if (!l.description_personnalisee.trim()) return false; // articles vides → exclus
      return true; // prix=0 autorisé (prestation gratuite, à définir, etc.)
    });
    if (lignesValides.length === 0) {
      alert('Ajoutez au moins une ligne avec une description.');
      return;
    }
    // ── Avertissement lignes à 0€ ─────────────────────────────────────────────
    const lignesZero = lignesValides.filter(
      l => l.type_ligne === 'article' && parseFloat(l.prix_unitaire || 0) === 0
    );
    if (lignesZero.length > 0) {
      const noms = lignesZero.map(l => `• ${l.description_personnalisee.trim()}`).join('\n');
      alert(
        `⚠️ Attention : ${lignesZero.length} article${lignesZero.length > 1 ? 's ont' : ' a'} un prix à 0,00 €\n\n`
        + noms
        + `\n\nCes lignes seront incluses dans le devis. Vérifiez avant d'envoyer.`
      );
    }

    setCreateLoading(true);
    try {
      let id_client = createForm.id_client;

      // Si nouveau client : le créer d'abord
      if (createMode === 'nouveau') {
        if (!createForm.nom) {
          alert('Le nom est obligatoire pour créer un nouveau client.');
          setCreateLoading(false);
          return;
        }
        const clientRes = await axios.post(`${API_URL}/clients`, {
          nom:          createForm.nom,
          prenom:       createForm.prenom,
          email:        createForm.email,
          telephone:    createForm.telephone,
          adresse:      createForm.adresse,
          ville:        createForm.ville,
          code_postal:  createForm.code_postal,
          pays:         createForm.pays,
          numero_tva:   createForm.numero_tva,
          type_client:  createForm.type_client,
          nom_societe:  createForm.nom_societe || null,
        }, config);
        id_client = clientRes.data.data?.id_client;
        if (!id_client) throw new Error('Échec création client');
        if (clientRes.data.mot_de_passe_temp) setTempPasswordInfo({ email: createForm.email, password: clientRes.data.mot_de_passe_temp });
        await fetchClients();
      }

      if (!id_client) {
        alert('Sélectionnez un client ou créez-en un nouveau.');
        setCreateLoading(false);
        return;
      }

      // Calcul des montants
      const sousTotalBrut = lignesValides.reduce((s, l) =>
        s + (parseFloat(l.quantite) || 0) * (parseFloat(l.prix_unitaire) || 0), 0);
      const montant_tva = lignesValides.reduce((s, l) => {
        const base = (parseFloat(l.quantite) || 0) * (parseFloat(l.prix_unitaire) || 0);
        return s + base * (parseFloat(l.tva) || 0) / 100;
      }, 0);

      const devisRes = await axios.post(`${API_URL}/devis`, {
        id_client,
        taux_tva:    createForm.taux_tva,
        notes:       createForm.notes,
        notes_pdf:   createForm.notes_pdf,
        montant_ht:  sousTotalBrut.toFixed(2),
        montant_tva: montant_tva.toFixed(2),
        montant_ttc: (sousTotalBrut + montant_tva).toFixed(2),
        // Échéancier flexible : null si non activé, tableau de paliers si activé
        config_echeancier: useEcheancier && echValide ? echPaliers : null,
        adresse_chantier: createForm.adresse_chantier || null,
        ville_chantier:   createForm.ville_chantier   || null,
        cp_chantier:      createForm.cp_chantier       || null,
        duplique_de_id_devis: duplicationSourceId || null,
    nom_societe: '' ,
        lignes:      (() => {
          let _sn = 0, _an = 0;
          return lignesValides.map((l) => {
            const tl = l.type_ligne || 'article';
            let ns = l.numero_section || '';
            if (tl === 'section')  { _sn++; _an = 0; ns = `${_sn}.`; }
            else if (tl === 'article') { _an++; ns = _sn > 0 ? `${_sn}.${_an}` : `${_an}`; }
            return {
              description_personnalisee: l.description_personnalisee,
              type_ligne:               tl,
              numero_section:           ns || null,
              quantite:                 parseFloat(l.quantite)      || 0,
              prix_unitaire:            parseFloat(l.prix_unitaire)  || 0,
              tva:                      parseFloat(l.tva)            || 0,
              montant_ligne:            (parseFloat(l.quantite||0) * parseFloat(l.prix_unitaire||0)).toFixed(2),
              unite:                    l.unite || null,
            };
          });
        })()
      }, config);
      if (devisRes.data.mot_de_passe_temp && !tempPasswordInfo) setTempPasswordInfo({ email: devisRes.data.compte_email, password: devisRes.data.mot_de_passe_temp });

      setShowCreateModal(false);
      resetCreateForm();
      fetchDevis();
      // Succès — le modal temp password s'affiche si un compte a été créé
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur création : ' + err.message);
    } finally {
      setCreateLoading(false);
    }
  };

  const resetCreateForm = () => {
    setCreateMode('existant');
    setCreateForm({ id_client: '', nom: '', prenom: '', email: '', telephone: '',
      adresse: '', ville: '', code_postal: '', pays: 'Belgique',
      numero_tva: '', type_client: 'particulier', taux_tva: 6, notes: '', notes_pdf: '',
      adresse_chantier: '', ville_chantier: '', cp_chantier: '' });
    setCreateLignes([{ description_personnalisee: '', quantite: 1, prix_unitaire: 0, tva: 6 }]);
    setUseEcheancier(false);
    setEchPaliers([
      { num: 1, label: 'Acompte',    pct: 40 },
      { num: 2, label: 'Avancement', pct: 50 },
      { num: 3, label: 'Solde',      pct: 10 },
    ]);
    setDuplicationSourceId(null);
  };

  // ─── DUPLICATION D'UN DEVIS (admin) ──────────────────────────────────────
  // Reprend client + lignes + chantier + échéancier dans le formulaire de
  // création existant. Notes internes et remarques PDF volontairement NON
  // reprises (confidentialité entre dossiers clients) — tout reste modifiable
  // avant validation, y compris le client lui-même.
  const dupliquerDevis = (d) => {
    setCreateMode('existant');
    setCreateForm({
      id_client: d.id_client, nom: '', prenom: '', email: '', telephone: '',
      adresse: '', ville: '', code_postal: '', pays: 'Belgique',
      numero_tva: '', type_client: 'particulier',
      taux_tva: d.taux_tva || 6, notes: '', notes_pdf: '',
      adresse_chantier: d.adresse_chantier || '',
      ville_chantier:   d.ville_chantier   || '',
      cp_chantier:      d.cp_chantier      || '',
    });
    const lignesSource = (d.LigneDevis || d.lignes || []);
    setCreateLignes(
      lignesSource.length > 0
        ? lignesSource.map(l => ({
            description_personnalisee: l.description_personnalisee || '',
            quantite:       l.quantite ?? 1,
            prix_unitaire:  l.prix_unitaire ?? 0,
            tva:            l.tva ?? 6,
            unite:          l.unite || '',
            type_ligne:     l.type_ligne || 'article',
            numero_section: l.numero_section || '',
          }))
        : [{ description_personnalisee: '', quantite: 1, prix_unitaire: 0, tva: 6 }]
    );
    const paliers = Array.isArray(d.config_echeancier) && d.config_echeancier.length > 0
      ? d.config_echeancier : null;
    setUseEcheancier(!!paliers);
    setEchPaliers(paliers || [
      { num: 1, label: 'Acompte',    pct: 40 },
      { num: 2, label: 'Avancement', pct: 50 },
      { num: 3, label: 'Solde',      pct: 10 },
    ]);
    setDuplicationSourceId(d.id_devis);
    setShowCreateModal(true);
  };

  // ─── VISIBILITÉ CLIENT D'UN BROUILLON (admin) ────────────────────────────
  const toggleVisibiliteClient = async (d) => {
    setVisibiliteLoading(d.id_devis);
    try {
      await axios.put(`${API_URL}/devis/${d.id_devis}`, { visible_client: !d.visible_client }, config);
      await fetchDevis();
    } catch (e) {
      alert(e.response?.data?.message || 'Erreur lors du changement de visibilité.');
    } finally {
      setVisibiliteLoading(null);
    }
  };

  const addLigneCreate = () =>
    setCreateLignes(prev => [...prev, { description_personnalisee: '', quantite: 1, prix_unitaire: 0, tva: 6 }]);

  const removeLigneCreate = (i) =>
    setCreateLignes(prev => prev.filter((_, idx) => idx !== i));

  const updateLigneCreate = (i, field, val) =>
    setCreateLignes(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: val } : l));

  // ─── ACTIONS ADMIN ───────────────────────────────────────────────────────────

  const handleDelete = async (id, statut) => {
    if (STATUTS_BLOQUE_SUPPRESSION.includes(statut)) {
      alert(`Impossible de supprimer un devis "${statut}".`);
      return;
    }
    if (!window.confirm('Supprimer ce devis définitivement ?')) return;
    try {
      await axios.delete(`${API_URL}/devis/${id}`, config);
      fetchDevis();
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  // Ouvrir la modal d'envoi personnalisé
  const openEnvoiModal = (d) => {
    if (!parseFloat(d.montant_ttc) || parseFloat(d.montant_ttc) <= 0) {
      return alert('❌ Impossible d\'envoyer un devis à 0 €.\nAjoutez des prestations avec un prix avant d\'envoyer.');
    }
    const nomClient = formatNomClient(d.Client);
    setEnvoiDevisData(d);
    setEnvoiForm({
      sujet:       `Votre devis ${d.numero_devis} — MKW Solutions`,
      message:     `Bonjour ${nomClient},\n\nSuite à notre entretien, veuillez trouver ci-joint votre devis ${d.numero_devis}.\n\nNous restons disponibles pour tout renseignement complémentaire.\n\nCordialement,\nMKW Solutions`,
      inclure_cgv: true,
    });
    setShowEnvoiModal(d.id_devis);
  };

  // Soumettre l'envoi email personnalisé
  const submitEnvoiEmail = async () => {
    if (!envoiDevisData) return;
    setEnvoiLoading(true);
    try {
      await axios.post(
        `${API_URL}/devis/${envoiDevisData.id_devis}/envoyer-email`,
        { sujet: envoiForm.sujet, message_personnalise: envoiForm.message, inclure_cgv: envoiForm.inclure_cgv },
        config
      );
      setShowEnvoiModal(null);
      fetchDevis();
      // Ouvrir le PDF dans un nouvel onglet pour vérification
      const res = await axios.get(`${API_URL}/devis/${envoiDevisData.id_devis}/pdf`, { ...config, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) {
      alert('Erreur envoi : ' + (err.response?.data?.message || err.message));
    } finally {
      setEnvoiLoading(false);
    }
  };

  const envoyerDevis = async (id, numero) => {
    try {
      await axios.post(`${API_URL}/devis/${id}/envoyer`, {}, config);
      fetchDevis();
      // Ouvrir automatiquement le PDF dans un nouvel onglet apres envoi
      const res = await axios.get(`${API_URL}/devis/${id}/pdf`, { ...config, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  // Envoyer PDF par email pour signature manuelle
  const envoyerManuel = async (id, numero, clientEmail) => {
    if (!isRealEmail(clientEmail)) {
      // Télécharger le PDF pour remise en main propre
      await downloadDevisPDF(id, numero);
      // Proposer de mettre le statut à 'envoye_manuel' pour déclencher 'Doc signé reçu'
      if (window.confirm(
        'Avez-vous remis le devis ' + numero + ' en main propre au client ?\n\n' +
        'Si oui, le statut passe à "Envoyé (signé)" et le bouton\n' +
        '"✅ Doc signé reçu" apparaîtra quand le client vous restitue le devis signé.'
      )) {
        try {
          await axios.post(`${API_URL}/devis/${id}/envoyer-manuel`, {}, config);
          await fetchDevis();
          alert('✅ Statut mis à jour. Cliquez sur "✅ Doc signé reçu" quand le devis vous est retourné signé.');
        } catch(e) { alert('Erreur: ' + (e.response?.data?.message || e.message)); }
      }
      return;
    }
    if (!window.confirm(`Envoyer le devis ${numero} en PDF à ${clientEmail} pour signature manuelle ?`)) return;
    try {
      await axios.post(`${API_URL}/devis/${id}/envoyer-manuel`, {}, config);
      alert(`✅ PDF envoyé à ${clientEmail}. Le client doit le retourner signé.`);
    } catch(e) {
      alert('Erreur envoi: ' + (e.response?.data?.message || e.message));
    }
  };

  const downloadDevisPDF = async (id, numero) => {
    try {
      const res = await axios.get(`${API_URL}/devis/${id}/pdf`, { ...config, responseType: 'blob' });

      // Si le serveur renvoie du JSON (erreur), l'afficher
      const contentType = res.headers['content-type'] || '';
      if (!contentType.includes('application/pdf')) {
        // Lire le JSON d'erreur depuis le blob
        const text = await new Promise(r => {
          const reader = new FileReader();
          reader.onload = () => r(reader.result);
          reader.readAsText(res.data);
        });
        let msg = 'Erreur serveur lors de la génération du PDF';
        try { msg = JSON.parse(text).message || msg; } catch {}
        alert('Erreur : ' + msg);
        return;
      }

      const url  = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `devis-${numero}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // Afficher le vrai message d'erreur axios
      const msg = err.response?.data
        ? (typeof err.response.data === 'string'
            ? err.response.data
            : err.response?.data?.message || JSON.stringify(err.response.data))
        : err.message;
      alert('Erreur PDF devis : ' + msg);
    }
  };

  // Facturation par échéancier — ouvre le modal délai paiement
  const factureEcheance = (id, numero, label) => {
    setDelaiJours('7');
    setDateLibreFacture('');
    setShowFactureModal({ id, numero, label });
  };

  const submitFactureEcheance = async () => {
    const { id, numero, label } = showFactureModal;
    try {
      const body = { numero_echeance: numero };
      if (delaiJours === 'libre') {
        body.date_echeance_libre = dateLibreFacture;
      } else {
        body.delai_paiement_jours = parseInt(delaiJours);
      }
      if (pendingRefs) {
        if (pendingRefs.fa)  body.facture_rectifiee = pendingRefs.fa;
        if (pendingRefs.nc)  body.note_credit_ref   = pendingRefs.nc;
        if (pendingRefs.mot) body.motif_correction  = pendingRefs.mot;
      }
      setShowFactureModal(null);
      await axios.post(`${API_URL}/devis/${id}/facturer-echeance`, body, config);
      alert(`✅ Facture ${label} créée et envoyée au client !`);
      fetchDevis();
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  const appliquerRemise = async (id) => {
    try {
      await axios.patch(`${API_URL}/devis/${id}/remise`, {
        remise: remiseData.pourcentage, raison_remise: remiseData.raison
      }, config);
      setShowRemiseModal(null);
      setRemiseData({ pourcentage: 0, raison: '' });
      fetchDevis();
    } catch (err) { alert(err.message); }
  };

  const appliquerTVA = async (id) => {
    try {
      await axios.patch(`${API_URL}/devis/${id}/tva`, { taux_tva: tvaValue }, config);
      // Sauvegarder les régimes TVA si définis
      if (Object.keys(tvaRegimes).length > 0) {
        await axios.patch(`${API_URL}/devis/${id}/tva-regimes`, { tva_regimes: tvaRegimes }, config);
      }
      setShowTVAModal(null);
      fetchDevis();
    } catch (err) { alert('Erreur TVA: ' + err.message); }
  };

  // ─── GESTION LIGNES ──────────────────────────────────────────────────────────

  const handleFieldChange = (idDevis, idLigne, field, value) => {
    setDevis(prev => prev.map(d => d.id_devis !== idDevis ? d : {
      ...d,
      LigneDevis: d.LigneDevis.map(l => {
        const lid = l.id_ligne_devis || l.id_ligne;
        return lid === idLigne ? { ...l, [field]: value } : l;
      })
    }));
  };

  const saveLigneUpdate = async (ligne, idDevis) => {
    const idLigne = ligne.id_ligne_devis || ligne.id_ligne;
    if (!idLigne) return alert('ID ligne manquant');
    try {
      const res = await axios.patch(`${API_URL}/devis/ligne/${idLigne}`, {
        quantite:                  parseFloat(ligne.quantite)      || 0,
        prix_unitaire:             parseFloat(ligne.prix_unitaire) || 0,
        tva:                       parseFloat(ligne.tva)            || 0,
        description_personnalisee: ligne.description_personnalisee || '',
        unite:                     ligne.unite                     || null,
        type_ligne:                ligne.type_ligne                || 'article',
        numero_section:            ligne.numero_section            || null,
        code_tranche:              ligne.code_tranche              || null,
      }, config);
      setEditingLigne(null);
      const updated = res.data.data;
      setDevis(prev => prev.map(d => d.id_devis !== idDevis ? d : {
        ...d,
        montant_ht:  updated.montant_ht,
        montant_tva: updated.montant_tva,
        montant_ttc: updated.montant_ttc,
        LigneDevis:  trierLignes(updated.LigneDevis || d.LigneDevis)
      }));
    } catch (err) { alert('Erreur sauvegarde: ' + err.message); }
  };

  const handleDeleteLigne = async (idLigne, idDevis) => {
    if (!window.confirm('Supprimer cette ligne ?')) return;
    try {
      const res = await axios.delete(`${API_URL}/devis/ligne/${idLigne}`, config);
      const updated = res.data.data;
      setDevis(prev => prev.map(d => d.id_devis !== idDevis ? d : {
        ...d,
        montant_ht:  updated.montant_ht,
        montant_tva: updated.montant_tva,
        montant_ttc: updated.montant_ttc,
        LigneDevis:  trierLignes(updated.LigneDevis || d.LigneDevis)
      }));
    } catch (err) { alert('Erreur suppression: ' + err.message); }
  };

  const handleAddLigneLibre = async (d) => {
    if (estVerrouille(d)) return alert('Devis verrouillé — aucune modification possible.');
    try {
      await axios.post(`${API_URL}/devis/${d.id_devis}/ligne-libre`, {}, config);
      fetchDevis();
    } catch (err) { alert('Erreur: ' + err.message); }
  };

  // ─── ACTIONS CLIENT ───────────────────────────────────────────────────────────

  // Ouvrir la modal de confirmation légale avant d'accepter
  const openAcceptModal = (devis) => {
    setAcceptCGV(false);
    setShowAcceptModal(devis);
  };

  // Soumettre l'acceptation avec IP + horodatage
  const submitAcceptation = async () => {
    if (!acceptCGV) return;
    if (!showAcceptModal) return;
    setAcceptLoading(true);
    try {
      // L'IP réelle est capturée côté serveur (cf-connecting-ip / x-forwarded-for)
      await axios.patch(
        `${API_URL}/devis/${showAcceptModal.id_devis}/statut`,
        {
          statut:          'accepte',
          cgv_acceptees:   true,
        },
        config
      );
      setShowAcceptModal(null);
      fetchDevis();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setAcceptLoading(false);
    }
  };

  const refuserDevis = async (id) => {
    if (!window.confirm('Refuser ce devis ?')) return;
    try {
      await axios.patch(`${API_URL}/devis/${id}/statut`, { statut: 'refuse' }, config);
      fetchDevis();
    } catch (err) { alert(err.response?.data?.message || err.message); }
  };

  // ─── PREUVE D'ACCEPTATION (admin) ─────────────────────────────────────────
  const downloadPreuve = async (id, numero, dateAccept) => {
    try {
      const res = await axios.get(`${API_URL}/devis/${id}/preuve-acceptation`, {
        ...config, responseType: 'blob'
      });
      const url  = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href  = url;
      const dateStr = dateAccept
        ? new Date(dateAccept).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `PREUVE_ACCEPTATION_${numero}_${dateStr}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Erreur génération preuve : ' + (err.response?.data?.message || err.message));
    }
  };

  // ─── HELPERS ─────────────────────────────────────────────────────────────────

  const getStatutBadge = (s) => {
    // Couleurs pour les statuts non présents dans theme.statut
    const extraStatuts = {
      envoye_manuel: { bg: '#e0f2fe', color: '#0369a1', label: '📬 Envoyé (signé)' },
    };
    const st = theme.statut[s] || extraStatuts[s] || { bg: '#eee', color: '#666', label: s };
    return (
      <span style={{ background: st.bg, color: st.color, padding: '3px 12px',
        borderRadius: '15px', fontSize: '12px', fontWeight: '600' }}>
        {st.label}
      </span>
    );
  };

  // ── Ouvrir modal avenant ─────────────────────────────────────────────────────
  const ouvrirAvenantModal = (devis) => {
    if (!['accepte', 'facture', 'facture_partielle'].includes(devis.statut)) {
      return alert('Un avenant peut être créé sur un devis accepté ou facturé.');
    }
    const paliers = (devis.config_echeancier && Array.isArray(devis.config_echeancier) && devis.config_echeancier.length > 0)
      ? devis.config_echeancier
      : [{ num: 1, label: 'Acompte', pct: 40 }, { num: 2, label: 'Avancement', pct: 50 }, { num: 3, label: 'Solde', pct: 10 }];
    setShowAvenantModal(devis);
    setAvenantNotes(`Avenant au devis ${devis.numero_devis}`);
    setAvenantUseEch(false);
    setAvenantPaliers(paliers);
  };

  // ── Soumettre avenant ─────────────────────────────────────────────────────────
  const soumettreAvenant = async () => {
    if (!showAvenantModal) return;
    const echSomme = avenantPaliers.reduce((s, p) => s + (parseFloat(p.pct) || 0), 0);
    if (avenantUseEch && Math.abs(echSomme - 100) > 0.01) {
      return alert(`Les paliers doivent totaliser 100% (actuellement ${echSomme.toFixed(1)}%)`);
    }
    setAvenantLoading(true);
    try {
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      const body = {
        notes: avenantNotes,
        config_echeancier: avenantUseEch ? avenantPaliers : undefined,
      };
      const res = await axios.post(`${API_URL}/devis/${showAvenantModal.id_devis}/avenant`, body, cfg);
      alert(`✅ ${res.data.message || 'Avenant créé.'}
L'avenant est en statut Brouillon.`);
      setShowAvenantModal(null);
      await fetchDevis();
    } catch (e) {
      alert('Erreur : ' + (e.response?.data?.message || e.message));
    } finally {
      setAvenantLoading(false);
    }
  };

  // Alias pour compatibilité avec les boutons existants
  // ── Débloquer un devis après note de crédit (correction TVA, etc.) ──────────
  const debloquerDevis = async (d) => {
    if (!window.confirm('Débloquer le devis ' + d.numero_devis + ' ?\n\n' +
      'Remet le devis en "Accepté" pour permettre une facture rectificative.\n' +
      '⚠️ Uniquement après avoir émis la note de crédit correspondante.')) return;

    // Références comptables pour la facture rectificative
    // Pré-remplir avec TOUTES les factures liées au devis (pas seulement [0])
    const facturesLiees = (d.Factures || []).map(f => f.numero_facture).filter(Boolean).join(', ');
    const factureAnnulee = window.prompt(
      'N° de la/des facture(s) annulée(s) :\n(Séparés par virgule si plusieurs)',
      facturesLiees || ''
    );
    if (factureAnnulee === null) return; // annulé

    // NC : l'utilisateur doit aller vérifier dans Notes de crédit
    // Afficher un rappel utile
    const numeroNC = window.prompt(
      'N° de la note de crédit émise (visible dans onglet Notes de crédit) :\nLaisser vide pour remplir plus tard.',
      ''
    );
    if (numeroNC === null) return; // annulé
    const motif = window.prompt(
      'Motif de la rectification :',
      'Correction taux TVA'
    );
    if (motif === null) return; // annulé

    const body = {};
    if (factureAnnulee.trim()) body.facture_annulee = factureAnnulee.trim();
    if (numeroNC.trim())       body.numero_nc       = numeroNC.trim();
    if (motif.trim())          body.motif           = motif.trim();

    try {
      const res = await axios.post(API_URL + '/devis/' + d.id_devis + '/debloquer', body, config);
      const refs = res.data.data || {};
      // Mémoriser les refs pour les passer automatiquement à la prochaine facture
      const storedRefs = {
        fa:  factureAnnulee.trim() || refs.factures_annulees || '',
        nc:  numeroNC.trim()       || refs.notes_credit      || '',
        mot: motif.trim()          || '',
      };
      setPendingRefs(storedRefs);
      const refsMsg = [
        storedRefs.fa  ? '📋 Factures annulées : ' + storedRefs.fa : null,
        storedRefs.nc  ? '🔄 Notes de crédit : '  + storedRefs.nc  : null,
      ].filter(Boolean).join('\n');
      alert('✅ ' + res.data.message +
        '\n\n' + (refsMsg || 'Cliquez maintenant sur "Facturer" dans ce devis pour émettre la facture rectificative.'));
      await fetchDevis();
      // Auto-ouvrir le panneau de ce devis pour accéder directement aux boutons Facturer
      setExpandedId(d.id_devis);
    } catch (e) {
      const err = e.response?.data;
      if (err?.data?.ecart > 0) {
        const msg = '⚠️ Les notes de crédit couvrent ' + Number(err.data.totalNC || 0).toFixed(2) +
          ' € sur ' + Number(err.data.totalFactures || 0).toFixed(2) + ' € de factures.\nForcer le déblocage ?';
        if (window.confirm(msg)) {
          try {
            const r2 = await axios.post(API_URL + '/devis/' + d.id_devis + '/debloquer',
              { ...body, force: true }, config);
            alert('✅ ' + r2.data.message); fetchDevis();
          } catch (e2) { alert('Erreur : ' + (e2.response?.data?.message || e2.message)); }
        }
      } else {
        alert('Erreur : ' + (err?.message || e.message));
      }
    }
  };

  const creerAvenant = ouvrirAvenantModal;

  const totalCreate = createLignes.reduce((s, l) => {
    if (l.type_ligne === 'section' || l.type_ligne === 'sous_total') return s;
    return s + (parseFloat(l.quantite||0) * parseFloat(l.prix_unitaire||0));
  }, 0);

  // ─── RENDU ───────────────────────────────────────────────────────────────────

  if (loading) return <div style={styles.loading}>⏳ Chargement...</div>;


  // ── Sauvegarder les notes / texte PDF d'un devis ─────────────────────────
  const saveNotes = async (id) => {
    setSavingNotes(true);
    try {
      await axios.patch(`${API_URL}/devis/${id}/notes`, {
        notes:     editingNotesTmp.notes,
        notes_pdf: editingNotesTmp.notes_pdf,
      }, config);
      setDevis(prev => prev.map(d => d.id_devis === id
        ? { ...d, notes: editingNotesTmp.notes, notes_pdf: editingNotesTmp.notes_pdf }
        : d
      ));
      setEditingNotesId(null);
    } catch(e) { alert('Erreur sauvegarde notes: ' + (e.response?.data?.message || e.message)); }
    setSavingNotes(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fb' }}>

      {/* ── MODAL MOT DE PASSE TEMPORAIRE (compte créé à la volée) ── */}
      {tempPasswordInfo && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, maxWidth: '420px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔑</div>
            <h2 style={{ margin: '0 0 8px', color: theme.primary }}>Nouveau compte créé !</h2>
            <p style={{ color: theme.textSecondary, fontSize: '14px', marginBottom: '20px' }}>Communiquez ces identifiants au client. Ils ne s'afficheront plus après fermeture.</p>
            <div style={{ background: theme.primaryBg, borderRadius: '10px', padding: '16px 20px', marginBottom: '20px', textAlign: 'left' }}>
              <div style={{ marginBottom: '10px' }}><span style={{ fontSize: '12px', color: '#666', display: 'block' }}>EMAIL</span><strong style={{ fontSize: '15px' }}>{tempPasswordInfo.email}</strong></div>
              <div><span style={{ fontSize: '12px', color: '#666', display: 'block' }}>MOT DE PASSE TEMPORAIRE</span><strong style={{ fontSize: '22px', color: theme.primary, letterSpacing: '2px' }}>{tempPasswordInfo.password}</strong></div>
            </div>
            <button onClick={() => { navigator.clipboard?.writeText(`Email: ${tempPasswordInfo.email}\nMot de passe: ${tempPasswordInfo.password}`); }} style={{ ...S.btnSecondary, width: '100%', marginBottom: '10px' }}>📋 Copier les identifiants</button>
            <button onClick={() => setTempPasswordInfo(null)} style={{ ...S.btnPrimary, width: '100%' }}>✅ J'ai noté, fermer</button>
            <p style={{ marginTop: '14px', fontSize: '12px', color: theme.danger }}>⚠️ Cette fenêtre ne s'affichera plus une fois fermée.</p>
          </div>
        </div>
      )}
      {/* ── MODAL REMISE ─────────────────────────────────────────── */}
      {showRemiseModal && (
        <div style={S.overlay}>
          <div style={S.modal}>
            <h2 style={{ color: theme.primary, margin: '0 0 20px' }}>🏷️ Appliquer une remise</h2>
            <label style={S.label}>Pourcentage (%)</label>
            <input type="number" min="0" max="100" value={remiseData.pourcentage}
              onChange={e => setRemiseData({ ...remiseData, pourcentage: e.target.value })}
              style={S.input} />
            <label style={S.label}>Raison (optionnel)</label>
            <textarea value={remiseData.raison} rows={3}
              onChange={e => setRemiseData({ ...remiseData, raison: e.target.value })}
              style={{ ...S.input, resize: 'vertical' }} />
            <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
              <button onClick={() => appliquerRemise(showRemiseModal)} style={S.btnSuccess}>Appliquer</button>
              <button onClick={() => setShowRemiseModal(null)} style={S.btnGrey}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL TVA ────────────────────────────────────────────── */}
      {showTVAModal && (
        <div style={S.overlay}>
          <div style={S.modal}>
            <h2 style={{ color: theme.primary, margin: '0 0 20px' }}>⚡ Modifier la TVA</h2>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
              {[0, 6, 12, 21].map(t => (
                <button key={t} onClick={() => setTVAValue(t)} style={{
                  padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600',
                  border: `2px solid ${tvaValue === t ? theme.primary : '#ddd'}`,
                  background: tvaValue === t ? theme.primaryBg : 'white',
                  color: tvaValue === t ? theme.primary : '#333'
                }}>{t}%</button>
              ))}
            </div>
            <input type="number" value={tvaValue}
              onChange={e => setTVAValue(Number(e.target.value))} style={S.input} />

            {/* ── Sélecteur régime TVA — apparaît si le taux a des variantes ── */}
            {getRegimesPourTaux(tvaValue).length > 1 && (
              <div style={{ marginTop: '14px', padding: '12px', background: '#f8fafc',
                border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b',
                  textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>
                  Base légale / Régime TVA {tvaValue}%
                </div>
                {getRegimesPourTaux(tvaValue).map(r => (
                  <label key={r.code} style={{ display: 'flex', alignItems: 'flex-start',
                    gap: '8px', marginBottom: '6px', cursor: 'pointer' }}>
                    <input type="radio" name="tva_regime"
                      checked={(tvaRegimes[String(tvaValue)] || '') === r.code}
                      onChange={() => setTvaRegimes(prev => ({ ...prev, [String(tvaValue)]: r.code }))}
                      style={{ marginTop: '2px', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#334155' }}>
                        {r.label}
                      </div>
                      <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '1px' }}>
                        {r.base}
                      </div>
                      {r.mention && (
                        <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px',
                          fontStyle: 'italic', maxWidth: '380px' }}>
                          {r.mention.length > 120 ? r.mention.slice(0, 120) + '…' : r.mention}
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}
            {/* Régime unique pour 21% — standard, aucun choix */}
            {/* Mention légale : affichée si un régime unique OU si un régime a été sélectionné */}
            {(() => {
              // Priorité : régime sélectionné → sa mention
              const selectedCode = tvaRegimes[String(tvaValue)];
              const selectedRegime = selectedCode && TVA_REGIMES[selectedCode];
              if (selectedRegime && selectedRegime.mention) {
                return (
                  <div style={{ marginTop: '10px', padding: '8px 12px', background: '#fef9f0',
                    border: '1px solid #fed7aa', borderRadius: '6px',
                    fontSize: '10px', color: '#92400e', fontStyle: 'italic' }}>
                    📋 {selectedRegime.mention}
                  </div>
                );
              }
              // Fallback : si régime unique pour ce taux
              const regimes = getRegimesPourTaux(tvaValue);
              if (regimes.length === 1 && regimes[0].mention) {
                return (
                  <div style={{ marginTop: '10px', padding: '8px 12px', background: '#fef9f0',
                    border: '1px solid #fed7aa', borderRadius: '6px',
                    fontSize: '10px', color: '#92400e', fontStyle: 'italic' }}>
                    📋 {regimes[0].mention}
                  </div>
                );
              }
              return null;
            })()}

            <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
              <button onClick={() => appliquerTVA(showTVAModal)} style={S.btnPrimary}>Enregistrer</button>
              <button onClick={() => setShowTVAModal(null)} style={S.btnGrey}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL CONFIRMATION ACCEPTATION (CLIENT) ─────────────────────── */}
      {showAcceptModal && (
        <div style={S.overlay} onClick={() => setShowAcceptModal(null)}>
          <div style={{ ...S.modal, maxWidth: '520px', width: '95%' }} onClick={e => e.stopPropagation()}>

            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '44px', marginBottom: '8px' }}>✅</div>
              <h2 style={{ margin: '0 0 6px', color: '#2e7d32', fontSize: '20px' }}>
                Confirmer l'acceptation
              </h2>
              <div style={{ color: '#666', fontSize: '14px' }}>
                Devis <strong>{showAcceptModal.numero_devis}</strong> —{' '}
                <strong>{parseFloat(showAcceptModal.montant_ttc || 0).toFixed(2)} € TTC</strong>
              </div>
            </div>

            {/* Récapitulatif légal */}
            <div style={{
              background: '#f9fbe7', border: '1px solid #c5e1a5', borderRadius: '10px',
              padding: '16px 18px', marginBottom: '18px', fontSize: '13px', lineHeight: '1.7',
            }}>
              <div style={{ fontWeight: '700', color: '#33691e', marginBottom: '10px' }}>
                📋 Récapitulatif de votre engagement
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td style={{ color: '#666', paddingBottom: '4px', width: '45%' }}>Devis :</td>
                    <td style={{ fontWeight: '600' }}>{showAcceptModal.numero_devis}</td>
                  </tr>
                  <tr>
                    <td style={{ color: '#666', paddingBottom: '4px' }}>Montant TTC :</td>
                    <td style={{ fontWeight: '700', color: '#1a237e' }}>
                      {parseFloat(showAcceptModal.montant_ttc || 0).toFixed(2)} EUR
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: '#666', paddingBottom: '4px' }}>Date et heure :</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '12px', color: '#333' }}>
                      {new Date().toLocaleString('fr-BE', {
                        timeZone: 'Europe/Brussels',
                        dateStyle: 'long', timeStyle: 'medium'
                      })}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: '#666' }}>CGV :</td>
                    <td style={{ color: '#2e7d32', fontWeight: '600' }}>
                      {acceptCGV ? '✓ Acceptées' : '⚠️ Non cochées'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Avertissement légal */}
            <div style={{
              background: '#fff3e0', borderRadius: '8px', padding: '12px 16px',
              marginBottom: '20px', fontSize: '12px', color: '#e65100', lineHeight: '1.6',
            }}>
              ⚖️ En confirmant, cette acceptation sera <strong>irrévocable</strong> et constituera
              un engagement contractuel ferme valant signature, conformément à la loi belge du
              21/07/2016. Un email de confirmation vous sera envoyé immédiatement.
            </div>

            {/* Boutons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowAcceptModal(null)}
                style={{ ...S.btnGrey, padding: '12px 24px', fontSize: '14px' }}>
                ← Annuler
              </button>
              <button
                onClick={submitAcceptation}
                disabled={acceptLoading || !acceptCGV}
                style={{
                  ...S.btnPrimary, background: '#2e7d32', padding: '12px 28px',
                  fontSize: '14px', fontWeight: '700',
                  opacity: acceptLoading || !acceptCGV ? 0.5 : 1,
                  cursor: acceptCGV ? 'pointer' : 'not-allowed',
                }}>
                {acceptLoading ? '⏳ Enregistrement…' : '✅ Confirmer l\'acceptation'}
              </button>
            </div>
            {!acceptCGV && (
              <p style={{ color: '#e53935', fontSize: '12px', marginTop: '8px', textAlign: 'right' }}>
                ⚠️ Vous devez cocher la case CGV pour confirmer
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── MODAL AVENANT ─────────────────────────────────────────────────────── */}
      {showAvenantModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1100,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px' }}
          onClick={e => e.target === e.currentTarget && setShowAvenantModal(null)}>
          <div style={{ background:'white',borderRadius:'14px',padding:'28px',maxWidth:'540px',width:'100%',boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ margin:'0 0 18px',color:'#1a237e',fontSize:'18px' }}>
              📝 Créer un avenant — {showAvenantModal.numero_devis}
            </h3>

            {/* Notes */}
            <label style={{ display:'block',fontWeight:'600',fontSize:'13px',marginBottom:'6px',color:'#444' }}>
              Note / objet de l'avenant
            </label>
            <textarea
              value={avenantNotes}
              onChange={e => setAvenantNotes(e.target.value)}
              rows={3}
              style={{ width:'100%',border:'1px solid #ddd',borderRadius:'8px',padding:'10px',fontSize:'13px',resize:'vertical',boxSizing:'border-box',marginBottom:'16px' }}
            />

            {/* Option: modifier l'échéancier */}
            <div
              onClick={() => setAvenantUseEch(v => !v)}
              style={{ display:'flex',alignItems:'center',gap:'10px',padding:'12px 14px',background:avenantUseEch?'#e8f5e9':'#f5f7fa',border:`2px solid ${avenantUseEch?'#27ae60':'#ddd'}`,borderRadius:'10px',cursor:'pointer',marginBottom:avenantUseEch?'14px':'20px' }}>
              <div style={{ width:'20px',height:'20px',borderRadius:'4px',background:avenantUseEch?'#27ae60':'#ddd',display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontWeight:'700',fontSize:'13px',flexShrink:0 }}>
                {avenantUseEch ? '✓' : ''}
              </div>
              <div>
                <div style={{ fontWeight:'600',fontSize:'13px',color:avenantUseEch?'#1b5e20':'#555' }}>Modifier l'échéancier pour cet avenant</div>
                <div style={{ fontSize:'12px',color:'#888' }}>{avenantUseEch ? 'Vous définissez un nouvel échéancier' : "Reprend l&apos;échéancier du devis parent"}</div>
              </div>
            </div>

            {/* Paliers editables — même UI que création devis */}
            {avenantUseEch && (
              <div style={{ marginBottom:'20px' }}>
                {/* Sélecteur nombre de paliers */}
                <div style={{ marginBottom:'12px' }}>
                  <label style={{ fontSize:'12px',fontWeight:'600',color:'#555',display:'block',marginBottom:'6px' }}>Nombre de paliers</label>
                  <div style={{ display:'flex',gap:'6px' }}>
                    {[1,2,3,4,5].map(n => (
                      <button key={n} type="button" onClick={() => setNbPaliersAvenant(n)}
                        style={{ padding:'6px 14px',borderRadius:'6px',border:'none',cursor:'pointer',fontWeight:'600',fontSize:'13px',
                          background:avenantPaliers.length===n?'#4f6d89':'#f0f2f5',
                          color:avenantPaliers.length===n?'white':'#555' }}>
                        {n}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize:'11px',color:'#888',marginTop:'4px' }}>
                    {avenantPaliers.length===1 ? "⚡ 1 palier = Facture complète (100%)" : "Le 1er palier est l'acompte. Chaque palier déclenche une facture."}
                  </div>
                </div>
                {/* Grille paliers */}
                <div style={{ background:'#f8fafc',borderRadius:'8px',padding:'10px',border:'1px solid #e2e8f0' }}>
                  <div style={{ display:'grid',gridTemplateColumns:'2fr 3fr 1fr',gap:'6px',marginBottom:'8px' }}>
                    <span style={{ fontSize:'11px',color:'#888',fontWeight:'600',padding:'0 4px' }}>PALIER</span>
                    <span style={{ fontSize:'11px',color:'#888',fontWeight:'600',padding:'0 4px' }}>LABEL</span>
                    <span style={{ fontSize:'11px',color:'#888',fontWeight:'600',padding:'0 4px',textAlign:'right' }}>%</span>
                  </div>
                  {avenantPaliers.map((pal, idx) => (
                    <div key={idx} style={{ display:'grid',gridTemplateColumns:'2fr 3fr 1fr',gap:'6px',marginBottom:'6px',alignItems:'center' }}>
                      <div style={{ padding:'5px 10px',borderRadius:'4px',fontSize:'12px',fontWeight:'700',textAlign:'center',
                        background:idx===0?'#fff3e0':idx===avenantPaliers.length-1?'#e8f5e9':'#e3f2fd',
                        color:idx===0?'#e65100':idx===avenantPaliers.length-1?'#1b5e20':'#1565c0' }}>
                        {idx===0&&avenantPaliers.length>1?`${idx+1}. Acompte`:idx===avenantPaliers.length-1&&avenantPaliers.length>1?`${idx+1}. Solde`:`${idx+1}.`}
                      </div>
                      <input type="text" value={pal.label}
                        onChange={e => updateAvenantPalier(idx,'label',e.target.value)}
                        style={{ border:'1px solid #ddd',borderRadius:'6px',padding:'7px 10px',fontSize:'13px',width:'100%',boxSizing:'border-box' }}
                      />
                      <input type="number" min="1" max="100" value={pal.pct}
                        onChange={e => updateAvenantPalier(idx,'pct',e.target.value)}
                        style={{ border:'1px solid #ddd',borderRadius:'6px',padding:'7px 8px',fontSize:'13px',textAlign:'right',width:'100%',boxSizing:'border-box' }}
                      />
                    </div>
                  ))}
                  {(() => {
                    const somme = avenantPaliers.reduce((s,p)=>s+(parseFloat(p.pct)||0),0);
                    const ok = Math.abs(somme-100)<0.01;
                    return (
                      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:'10px',paddingTop:'8px',borderTop:'1px solid #e2e8f0' }}>
                        <span style={{ fontSize:'12px',color:'#666' }}>Somme totale :</span>
                        <span style={{ fontWeight:'700',fontSize:'14px',color:ok?'#27ae60':'#e74c3c',background:ok?'#e8f5e9':'#fdecea',padding:'3px 10px',borderRadius:'12px' }}>
                          {somme.toFixed(0)}% {ok?'✓ OK':'⚠️ doit être 100%'}
                        </span>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            <div style={{ display:'flex',gap:'10px' }}>
              <button onClick={() => setShowAvenantModal(null)}
                style={{ flex:1,padding:'11px',border:'1px solid #ddd',borderRadius:'8px',background:'white',cursor:'pointer',fontSize:'14px' }}>
                Annuler
              </button>
              <button onClick={soumettreAvenant} disabled={avenantLoading}
                style={{ flex:2,padding:'11px',border:'none',borderRadius:'8px',background:'#1a237e',color:'white',cursor:'pointer',fontSize:'14px',fontWeight:'700',opacity:avenantLoading?0.7:1 }}>
                {avenantLoading ? '⏳ Création…' : '📝 Créer l&apos;avenant'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL DÉLAI PAIEMENT FACTURE ─────────────────────────────────────── */}
      {showFactureModal && (
        <div style={S.overlay} onClick={() => setShowFactureModal(null)}>
          <div style={{ ...S.modal, maxWidth: '380px' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ color: theme.primary, margin: '0 0 6px', fontSize: '17px' }}>
              🧾 Émettre la facture
            </h2>
            <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#555' }}>
              {showFactureModal.label} — choisissez le délai de paiement&nbsp;:
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '14px' }}>
              {[
                { val: '7',     label: '7 jours' },
                { val: '14',    label: '14 jours' },
                { val: '30',    label: '30 jours' },
                { val: 'libre', label: '📅 Date libre' },
              ].map(opt => (
                <button
                  key={opt.val}
                  onClick={() => setDelaiJours(opt.val)}
                  style={{
                    padding: '10px 8px',
                    border: delaiJours === opt.val ? `2px solid ${theme.primary}` : '1.5px solid #ddd',
                    borderRadius: '8px',
                    background: delaiJours === opt.val ? '#eaf0f8' : 'white',
                    color: delaiJours === opt.val ? theme.primary : '#444',
                    fontWeight: delaiJours === opt.val ? '700' : '500',
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {delaiJours === 'libre' && (
              <div style={{ marginBottom: '14px' }}>
                <label style={S.label}>Date d'échéance</label>
                <input
                  type="date"
                  value={dateLibreFacture}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={e => setDateLibreFacture(e.target.value)}
                  style={S.input}
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '4px' }}>
              <button
                onClick={() => setShowFactureModal(null)}
                style={{ ...S.btnGrey, flex: '1' }}>
                Annuler
              </button>
              <button
                onClick={submitFactureEcheance}
                disabled={delaiJours === 'libre' && !dateLibreFacture}
                style={{
                  ...S.btnSuccess, flex: '2',
                  opacity: (delaiJours === 'libre' && !dateLibreFacture) ? 0.5 : 1,
                }}>
                ✅ Émettre la facture
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL ENVOI EMAIL PERSONNALISÉ ──────────────────────────────────── */}
      {showEnvoiModal && envoiDevisData && (
        <div style={S.overlay} onClick={() => setShowEnvoiModal(null)}>
          <div style={{ ...S.modal, maxWidth: '580px', width: '95%' }} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h2 style={{ margin: '0 0 4px', color: theme.primary, fontSize: '18px' }}>
                  📤 Envoyer le devis
                </h2>
                <div style={{ fontSize: '13px', color: theme.textMuted }}>
                  {envoiDevisData.numero_devis} → <strong style={{ color: '#333' }}>
                    {envoiDevisData.Client?.email || '(email manquant)'}
                  </strong>
                </div>
              </div>
              <button onClick={() => setShowEnvoiModal(null)}
                style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#999' }}>✕</button>
            </div>

            {/* Récap devis */}
            <div style={{ background: '#e8eaf6', borderRadius: '8px', padding: '12px 16px', marginBottom: '18px', fontSize: '13px', display: 'flex', gap: '24px' }}>
              <span>📄 <strong>{envoiDevisData.numero_devis}</strong></span>
              <span>👤 {formatNomClient(envoiDevisData.Client)}</span>
              <span>💶 <strong>{parseFloat(envoiDevisData.montant_ttc || 0).toFixed(2)} € TTC</strong></span>
            </div>

            {/* Sujet */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '6px' }}>
                SUJET DE L'EMAIL
              </label>
              <input
                value={envoiForm.sujet}
                onChange={e => setEnvoiForm(f => ({ ...f, sujet: e.target.value }))}
                style={{
                  width: '100%', padding: '10px 12px', border: '1px solid #ddd',
                  borderRadius: '7px', fontSize: '13px', boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Message personnalisé */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '6px' }}>
                MESSAGE PERSONNALISÉ <span style={{ fontWeight: '400', color: '#999' }}>(optionnel)</span>
              </label>
              <textarea
                value={envoiForm.message}
                onChange={e => setEnvoiForm(f => ({ ...f, message: e.target.value }))}
                rows={6}
                style={{
                  width: '100%', padding: '10px 12px', border: '1px solid #ddd',
                  borderRadius: '7px', fontSize: '13px', boxSizing: 'border-box',
                  resize: 'vertical', fontFamily: 'inherit', lineHeight: '1.5',
                }}
              />
            </div>

            {/* Option CGV */}
            <label style={{
              display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer',
              background: '#f5f7fb', borderRadius: '8px', padding: '12px 16px',
              marginBottom: '20px', fontSize: '13px', border: '1px solid #e0e0e0',
            }}>
              <input
                type="checkbox"
                checked={envoiForm.inclure_cgv}
                onChange={e => setEnvoiForm(f => ({ ...f, inclure_cgv: e.target.checked }))}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <div>
                <div style={{ fontWeight: '600', color: '#333' }}>📋 Inclure les CGV en annexe</div>
                <div style={{ color: '#888', fontSize: '12px', marginTop: '2px' }}>
                  Les Conditions Générales de Vente sont jointes en page 2 du PDF
                </div>
              </div>
            </label>

            {/* Pièce jointe info */}
            <div style={{ background: '#e8f5e9', borderRadius: '8px', padding: '10px 16px', marginBottom: '20px', fontSize: '12px', color: '#2e7d32', display: 'flex', gap: '8px', alignItems: 'center' }}>
              📎 <span><strong>devis-{envoiDevisData.numero_devis}.pdf</strong> sera joint automatiquement</span>
            </div>

            {/* Boutons */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowEnvoiModal(null)}
                style={{ ...S.btnGrey, padding: '11px 22px', fontSize: '14px' }}>
                Annuler
              </button>
              <button
                onClick={submitEnvoiEmail}
                disabled={envoiLoading || !envoiDevisData.Client?.email}
                style={{
                  ...S.btnPrimary, background: theme.info, padding: '11px 28px',
                  fontSize: '14px', fontWeight: '700', opacity: envoiLoading ? 0.7 : 1,
                }}>
                {envoiLoading ? '⏳ Envoi…' : '📤 Envoyer'}
              </button>
            </div>
            {!envoiDevisData.Client?.email && (
              <p style={{ color: '#e53935', fontSize: '12px', marginTop: '8px', textAlign: 'right' }}>
                ⚠️ Ce client n'a pas d'adresse email enregistrée
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── MODAL CRÉATION DEVIS (ADMIN) ─────────────────────────── */}
      {showCreateModal && (
        <div style={{ ...S.overlay, alignItems: 'flex-start', paddingTop: isMobile ? '0' : '24px', overflowY: 'auto' }}>
          <div style={{ ...S.modal, maxWidth: '680px', width: isMobile ? '98vw' : '95%', maxHeight: '92dvh', overflowY: 'auto', padding: isMobile ? '16px' : '28px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: isMobile ? '12px' : '20px' }}>
              <h2 style={{ color: theme.primary, margin: 0 }}>
                {duplicationSourceId ? '🔁 Dupliquer un devis' : '📄 Créer un devis'}
              </h2>
              <button onClick={() => { setShowCreateModal(false); resetCreateForm(); }}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>✕</button>
            </div>

            <form onSubmit={handleCreateDevis}>

              {/* ── SECTION CLIENT ── */}
              <div style={S.section}>
                <h3 style={S.sectionTitle}>👤 Client</h3>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                  {['existant', 'nouveau'].map(m => (
                    <button key={m} type="button" onClick={() => setCreateMode(m)} style={{
                      flex: 1, padding: '9px', border: `2px solid ${createMode === m ? theme.primary : '#ddd'}`,
                      borderRadius: '7px', background: createMode === m ? theme.primaryBg : 'white',
                      color: createMode === m ? theme.primary : '#666',
                      fontWeight: '600', cursor: 'pointer', fontSize: '13px'
                    }}>
                      {m === 'existant' ? '📋 Client existant' : '➕ Nouveau client'}
                    </button>
                  ))}
                </div>

                {createMode === 'existant' ? (
                  <div>
                    <label style={S.label}>Sélectionner le client *</label>
                    <select value={createForm.id_client}
                      onChange={e => setCreateForm({ ...createForm, id_client: e.target.value })}
                      style={S.input} required>
                      <option value="">— Choisir un client —</option>
                      {clients.map(c => (
                        <option key={c.id_client} value={c.id_client}>
                          {formatNomClient(c)}{isRealEmail(c.email) ? ` — ${c.email}` : ' — sans email'}
                          {c.numero_tva ? ` (TVA: ${c.numero_tva})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                    {[
                      { key: 'nom',         label: 'Nom *',         required: true  },
                      { key: 'prenom',      label: 'Prénom',        required: false },
                      { key: 'email',       label: 'Email (optionnel)', required: false, full: true },
                      { key: 'telephone',   label: 'Téléphone',     required: false },
                      { key: 'adresse',     label: 'Adresse',       required: false, full: true },
                      { key: 'ville',       label: 'Ville',         required: false },
                      { key: 'code_postal', label: 'Code postal',   required: false },
                      { key: 'numero_tva',  label: 'N° TVA',        required: false },
                    ].map(f => (
                      <div key={f.key} style={f.full ? { gridColumn: '1 / -1' } : {}}>
                        <label style={S.label}>{f.label}</label>
                        <input type={f.key === 'email' ? 'email' : 'text'}
                          value={createForm[f.key]}
                          onChange={e => setCreateForm({ ...createForm, [f.key]: e.target.value })}
                          required={f.required}
                          style={S.input} />
                      </div>
                    ))}
                    <div>
                      <label style={S.label}>Type client</label>
                      <select value={createForm.type_client}
                        onChange={e => setCreateForm({ ...createForm, type_client: e.target.value })}
                        style={S.input}>
                        <option value="particulier">Particulier</option>
                        <option value="professionnel">Professionnel</option>
                      </select>
                    </div>
                    {/* Nom société (PRO seulement) */}
                    {createForm.type_client === 'professionnel' && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={S.label}>Nom de la société *</label>
                        <input type="text"
                          value={createForm.nom_societe || ''}
                          onChange={e => setCreateForm({ ...createForm, nom_societe: e.target.value })}
                          placeholder="ex: BeApart NV, MKW Solutions SA..."
                          style={S.input} />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ── SECTION LIGNES ── */}
              <div style={S.section}>
                <h3 style={S.sectionTitle}>📋 Lignes du devis</h3>
                {(() => {
                  // Numérotation hiérarchique calculée au rendu
                  let secNum = 0, artNum = 0, secTotal = 0;
                  return createLignes.map((l, i) => {
                    const isSec  = l.type_ligne === 'section';
                    const isST   = l.type_ligne === 'sous_total';
                    const isArt  = !isSec && !isST;
                    let numLabel = '';

                    if (isSec)  { secNum++; artNum = 0; secTotal = 0; numLabel = `${secNum}.`; }
                    else if (isST) { numLabel = ''; }
                    else        { artNum++; numLabel = secNum > 0 ? `${secNum}.${artNum}` : `${artNum}`; secTotal += parseFloat(l.quantite||0)*parseFloat(l.prix_unitaire||0); }

                    // numero_section calculé à l'envoi API — pas de setState pendant le rendu

                    return (
                      <div key={i} style={{
                        display: 'flex', flexDirection: 'column', gap: 0,
                        marginBottom: 6, borderRadius: 8,
                        background: isSec ? '#dbeafe' : isST ? '#ede9fe' : 'transparent',
                        border: isSec ? '1px solid #bfdbfe' : isST ? '1px solid #ddd6fe' : 'none',
                        padding: isSec || isST ? '8px 10px' : 0,
                      }}>
                        {/* Label section ou sous-total */}
                        {isSec && (
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#1e40af', marginBottom: 4 }}>
                            📂 SECTION {secNum}
                          </div>
                        )}
                        {isST && (
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', marginBottom: 4 }}>
                            Σ SOUS-TOTAL — calculé automatiquement ({secTotal.toFixed(2)} €)
                          </div>
                        )}

                        <div style={isMobile ? {
                          display:'flex', flexDirection:'column', gap:'8px'
                        } : {
                          display:'grid',
                          gridTemplateColumns: isArt
                            ? 'minmax(120px,3fr) 60px 50px minmax(80px,1.1fr) 56px auto'
                            : 'minmax(120px,1fr) auto',
                          gap:'6px', alignItems:'end'
                        }}>
                          <div>
                            {i === 0 && <label style={S.label}>Description</label>}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              {(numLabel) && (
                                <span style={{ fontSize: 11, fontWeight: 700, color: isSec ? '#1e40af' : '#94a3b8', whiteSpace: 'nowrap' }}>
                                  {numLabel}
                                </span>
                              )}
                              {(isSec || isST) ? (
                                <input type="text" value={l.description_personnalisee}
                                  placeholder={
                                    isSec ? `Titre de la section (ex: Détection incendie - Matériel)` :
                                    `Libellé (ex: Sous-total section ${secNum})`
                                  }
                                  onChange={e => updateLigneCreate(i, 'description_personnalisee', e.target.value)}
                                  style={{ ...S.input, fontSize: 13, fontWeight: 700, flex: 1 }} />
                              ) : (
                                <div style={{ flex: 1, display:'flex', flexDirection:'column', gap:'3px' }}>
                                  <div style={{ display:'flex', gap:'4px', alignItems:'center' }}>
                                    {[{m:'**',bl:'B',t:'Gras (**texte**)',bs:{fontWeight:'900'}},
                                      {m:'_', bl:'I',t:'Italique (_texte_)',bs:{fontStyle:'italic',fontWeight:'600'}},
                                      {m:'__',bl:'U',t:'Souligné (__texte__)',bs:{textDecoration:'underline',fontWeight:'600'}}]
                                     .map(({m,bl,t,bs}) => (
                                      <button key={bl} type="button" title={t}
                                        onMouseDown={e => {
                                          e.preventDefault();
                                          const ta = document.querySelector('[data-create-idx="' + i + '"]');
                                          if (!ta) return;
                                          const sv = ta.selectionStart, ev = ta.selectionEnd;
                                          const val = l.description_personnalisee || '';
                                          const sel = val.substring(sv, ev);
                                          const ins = sel ? m+sel+m : m+(bl==='B'?'gras':bl==='I'?'italique':'souligné')+m;
                                          updateLigneCreate(i, 'description_personnalisee', val.substring(0,sv)+ins+val.substring(ev));
                                        }}
                                        style={{ padding:'2px 8px', borderRadius:'4px', border:'1px solid #cbd5e1',
                                          background:'white', cursor:'pointer', fontSize:'12px',
                                          lineHeight:1, color:'#1e3a5f', flexShrink:0, ...bs }}>
                                        {bl}
                                      </button>
                                    ))}
                                    <span style={{ fontSize:'9px', color:'#9ca3af' }}>**gras** _ital_ __souligné__ · ↵</span>
                                  </div>
                                  <textarea
                                    data-create-idx={i}
                                    value={l.description_personnalisee}
                                    placeholder="Désignation de la prestation…"
                                    onChange={e => updateLigneCreate(i, 'description_personnalisee', e.target.value)}
                                    rows={2}
                                    style={{ ...S.input, fontSize: 13, resize:'vertical',
                                      minHeight:'44px', fontFamily:'inherit', lineHeight:1.4 }} />
                                </div>
                              )}
                            </div>
                          </div>

                          {isArt && (
                            <div style={isMobile ? {
                              display:'grid',
                              gridTemplateColumns:'58px 50px 1fr 54px 34px',
                              gap:'5px', alignItems:'end',
                            } : { display:'contents' }}>
                              <div>
                                {i === 0 && <label style={S.label}>Unité</label>}
                                <select value={l.unite || ''}
                                  onChange={e => updateLigneCreate(i, 'unite', e.target.value)}
                                  style={{ ...S.input, fontSize: 12, cursor: 'pointer', minWidth: 52 }}>
                                  <option value="">—</option>
                                  {['h','FF','stuk','m²','ml','m','kg','kW','kWh','jr','pcs'].map(u =>
                                    <option key={u} value={u}>{u}</option>
                                  )}
                                </select>
                              </div>
                              <div>
                                {i === 0 && <label style={S.label}>Qté</label>}
                                <input type="number" value={l.quantite} min="0" step="0.01"
                                  onChange={e => updateLigneCreate(i, 'quantite', e.target.value)}
                                  style={{ ...S.input, minWidth: 52 }} placeholder="Qté" />
                              </div>
                              <div>
                                {i === 0 && <label style={S.label}>Prix unit.</label>}
                                <input type="number" value={l.prix_unitaire} min="0" step="0.01"
                                  onChange={e => updateLigneCreate(i, 'prix_unitaire', e.target.value)}
                                  style={S.input} placeholder="PU (€)" />
                              </div>
                              <div>
                                {i === 0 && <label style={S.label}>TVA%</label>}
                                <select value={l.tva}
                                  onChange={e => updateLigneCreate(i, 'tva', Number(e.target.value))}
                                  style={S.input}>
                                  {[0, 6, 12, 21].map(t => <option key={t} value={t}>{t}%</option>)}
                                </select>
                              </div>
                              {/* Delete button in numeric row on mobile */}
                              {isMobile && createLignes.length > 1 && (
                                <div style={{ display:'flex', alignItems:'flex-end' }}>
                                  <button type="button"
                                    onClick={() => setCreateLignes(prev => prev.filter((_,j) => j !== i))}
                                    style={{ ...S.btnDanger, padding:'9px 11px' }}>🗑️</button>
                                </div>
                              )}
                            </div>
                          )}
                          {/* Delete button for desktop (grid cell) */}
                          {!isMobile && (
                            <div style={{ display:'flex', alignItems:'flex-end' }}>
                              {createLignes.length > 1 && (
                                <button type="button"
                                  onClick={() => setCreateLignes(prev => prev.filter((_,j) => j !== i))}
                                  style={{ ...S.btnDanger, padding: '9px 11px' }}>🗑️</button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
                <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                  <button type="button" onClick={addLigneCreate}
                    style={{ ...S.btnSuccess, fontSize: '12px' }}>
                    ＋ Article
                  </button>
                  <button type="button"
                    onClick={() => {
                      setCreateLignes(prev => {
                        // Vérifier si on est déjà dans une section avec des articles
                        const hasSection = prev.some(l => l.type_ligne === 'section');
                        const hasArticlesAfterLastSection = (() => {
                          // Trouver le dernier index de section
                          let lastSecIdx = -1;
                          prev.forEach((l, i) => { if (l.type_ligne === 'section') lastSecIdx = i; });
                          // Y a-t-il des articles après la dernière section?
                          return lastSecIdx >= 0 && prev.slice(lastSecIdx + 1).some(
                            l => l.type_ligne === 'article' && parseFloat(l.prix_unitaire || 0) > 0
                          );
                        })();
                        const alreadyHasSousTotal = prev.length > 0 && 
                          prev[prev.length - 1].type_ligne === 'sous_total';

                        // Auto-insérer un sous-total si nécessaire
                        const toAdd = [];
                        if (hasSection && hasArticlesAfterLastSection && !alreadyHasSousTotal) {
                          toAdd.push({
                            description_personnalisee: 'Sous-total',
                            quantite: 0, prix_unitaire: 0, tva: 0,
                            type_ligne: 'sous_total', numero_section: ''
                          });
                        }
                        // Ajouter la nouvelle section
                        toAdd.push({
                          description_personnalisee: '',
                          quantite: 0, prix_unitaire: 0, tva: 0,
                          type_ligne: 'section', numero_section: ''
                        });
                        return [...prev, ...toAdd];
                      });
                    }}
                    style={{ ...S.btnSuccess, fontSize: '12px', background: '#1e40af' }}>
                    📂 Ajouter section
                  </button>
                  <button type="button"
                    onClick={() => setCreateLignes(prev => [...prev, {
                      description_personnalisee: 'Sous-total', quantite: 0,
                      prix_unitaire: 0, tva: 0, type_ligne: 'sous_total', numero_section: ''
                    }])}
                    style={{ ...S.btnSuccess, fontSize: '12px', background: '#7c3aed' }}>
                    Σ Sous-total
                  </button>
                </div>
                <div style={{ textAlign: 'right', marginTop: '12px', fontSize: '14px', color: theme.textSecondary }}>
                  Sous-total HT : <strong>{totalCreate.toFixed(2)} €</strong>
                </div>
              </div>

              {/* ── SECTION OPTIONS ── */}
              <div style={S.section}>
                <h3 style={S.sectionTitle}>⚙️ Options</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={S.label}>TVA globale par défaut</label>
                    <select value={createForm.taux_tva}
                      onChange={e => setCreateForm({ ...createForm, taux_tva: Number(e.target.value) })}
                      style={S.input}>
                      {[0, 6, 12, 21].map(t => <option key={t} value={t}>{t}%</option>)}
                    </select>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={S.label}>
                      📝 Remarques visibles sur le devis PDF
                      <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 6 }}>
                        (affiché avant "Merci pour votre confiance!")
                      </span>
                    </label>
                    <textarea value={createForm.notes_pdf}
                      onChange={e => setCreateForm({ ...createForm, notes_pdf: e.target.value })}
                      placeholder="Ex: Prix valables sous réserve d'acceptation dans les 30 jours. Travaux à réaliser en dehors des heures de production..."
                      rows={3}
                      style={{ ...S.input, resize: 'vertical', fontFamily: 'inherit' }} />
                  </div>
                  <div>
                    <label style={S.label}>Notes internes (non imprimées)</label>
                    <input type="text" value={createForm.notes}
                      onChange={e => setCreateForm({ ...createForm, notes: e.target.value })}
                      placeholder="Notes internes uniquement..." style={S.input} />
                  </div>
                  {/* ── Adresse du chantier (lieu de la prestation) ── */}
                  <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #e5e7eb', paddingTop: '12px', marginTop: '4px' }}>
                    <h4 style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: '700', color: '#4a7bb5' }}>
                      📍 Adresse du chantier (optionnel — si différente de l'adresse du client)
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: '8px' }}>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={S.label}>Adresse du chantier</label>
                        <input type="text" value={createForm.adresse_chantier}
                          onChange={e => setCreateForm({ ...createForm, adresse_chantier: e.target.value })}
                          placeholder="ex: Rue du Progrès 12" style={S.input} />
                      </div>
                      <div>
                        <label style={S.label}>Ville</label>
                        <input type="text" value={createForm.ville_chantier}
                          onChange={e => setCreateForm({ ...createForm, ville_chantier: e.target.value })}
                          placeholder="ex: Bruxelles" style={S.input} />
                      </div>
                      <div>
                        <label style={S.label}>Code postal</label>
                        <input type="text" value={createForm.cp_chantier}
                          onChange={e => setCreateForm({ ...createForm, cp_chantier: e.target.value })}
                          placeholder="ex: 1000" style={S.input} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── SECTION ÉCHÉANCIER ── */}
              <div style={S.section}>
                <h3 style={S.sectionTitle}>📅 Facturation (échéancier)</h3>

                {/* Toggle activation */}
                <div
                  onClick={() => setUseEcheancier(v => !v)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    cursor: 'pointer', padding: '10px 12px', borderRadius: '8px',
                    background: useEcheancier ? '#e8f5e9' : '#f5f7fa',
                    border: `2px solid ${useEcheancier ? '#27ae60' : '#ddd'}`,
                    marginBottom: useEcheancier ? '14px' : '0',
                    transition: 'all 0.2s',
                  }}>
                  <div style={{
                    width: '20px', height: '20px', borderRadius: '4px',
                    background: useEcheancier ? '#27ae60' : '#ddd',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontSize: '13px', flexShrink: 0,
                  }}>
                    {useEcheancier ? '✓' : ''}
                  </div>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '13px', color: useEcheancier ? '#1b5e20' : '#555' }}>
                      Activer l'échéancier de facturation
                    </div>
                    <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                      {useEcheancier ? 'La facture sera fractionnée selon les paliers définis ci-dessous' : 'Facture unique (100% en une fois)'}
                    </div>
                  </div>
                </div>

                {useEcheancier && (
                  <div>
                    {/* Sélecteur du nombre de paliers */}
                    <div style={{ marginBottom: '12px' }}>
                      <label style={S.label}>Nombre de paliers</label>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {[1, 2, 3, 4, 5].map(n => (
                          <button key={n} type="button"
                            onClick={() => setNbPaliers(n)}
                            style={{
                              padding: '6px 14px', borderRadius: '6px', border: 'none',
                              cursor: 'pointer', fontWeight: '600', fontSize: '13px',
                              background: echPaliers.length === n ? '#4f6d89' : '#f0f2f5',
                              color:      echPaliers.length === n ? 'white'    : '#555',
                            }}>
                            {n}
                          </button>
                        ))}
                      </div>
                      <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                        {echPaliers.length === 1 ? '⚡ 1 palier = facture complète avec tous les postes' : `Le premier palier est l'acompte. Chaque palier déclenche une facture distincte.`}
                      </div>
                    </div>

                    {/* Tableau des paliers */}
                    <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px', border: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 3fr 1fr', gap: '6px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '11px', color: '#888', fontWeight: '600', padding: '0 4px' }}>PALIER</span>
                        <span style={{ fontSize: '11px', color: '#888', fontWeight: '600', padding: '0 4px' }}>LABEL</span>
                        <span style={{ fontSize: '11px', color: '#888', fontWeight: '600', padding: '0 4px', textAlign: 'right' }}>%</span>
                      </div>
                      {echPaliers.map((pal, idx) => (
                        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 3fr 1fr', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
                          <div style={{
                            padding: '5px 10px', borderRadius: '4px', fontSize: '12px',
                            fontWeight: '700', textAlign: 'center',
                            background: idx === 0 ? '#fff3e0' : idx === echPaliers.length - 1 ? '#e8f5e9' : '#e3f2fd',
                            color:      idx === 0 ? '#e65100' : idx === echPaliers.length - 1 ? '#1b5e20' : '#1565c0',
                          }}>
                            {idx === 0 && echPaliers.length > 1 ? `${idx+1}. Acompte` : idx === echPaliers.length - 1 && echPaliers.length > 1 ? `${idx+1}. Solde` : `${idx+1}.`}
                          </div>
                          <input
                            type="text"
                            value={pal.label}
                            onChange={e => updateEchPalier(idx, 'label', e.target.value)}
                            placeholder={`Palier ${idx+1}`}
                            style={{ ...S.input, margin: 0, fontSize: '13px' }}
                          />
                          <input
                            type="number"
                            min="1" max="100" step="1"
                            value={pal.pct}
                            onChange={e => updateEchPalier(idx, 'pct', e.target.value)}
                            style={{ ...S.input, margin: 0, textAlign: 'right', fontSize: '13px' }}
                          />
                        </div>
                      ))}

                      {/* Indicateur somme */}
                      <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        marginTop: '10px', paddingTop: '8px', borderTop: '1px solid #e2e8f0',
                      }}>
                        <span style={{ fontSize: '12px', color: '#666' }}>Somme totale :</span>
                        <span style={{
                          fontWeight: '700', fontSize: '14px',
                          color: echValide ? '#27ae60' : '#e74c3c',
                          background: echValide ? '#e8f5e9' : '#fdecea',
                          padding: '3px 10px', borderRadius: '12px',
                        }}>
                          {echSomme.toFixed(0)}% {echValide ? '✓ OK' : `⚠️ doit être 100%`}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <button type="submit" disabled={createLoading || (useEcheancier && !echValide)}
                  style={{ flex: 1, ...S.btnPrimary, opacity: (createLoading || (useEcheancier && !echValide)) ? 0.7 : 1, padding: '12px' }}>
                  {createLoading ? '⏳ Création...' : '✅ Créer le devis'}
                </button>
                <button type="button" onClick={() => { setShowCreateModal(false); resetCreateForm(); }}
                  style={{ ...S.btnGrey, padding: '12px 20px' }}>
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── HEADER PAGE ──────────────────────────────────────────── */}
      <div style={{ background: theme.gradientHeader, padding: isMobile ? '14px 14px' : '20px 24px', color: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <h1 style={{ margin: 0, fontSize: '22px' }}>
            📄 {isAdmin ? 'Gestion des Devis' : 'Mes Devis'}
          </h1>
          {isAdmin && (
            <button onClick={() => setShowCreateModal(true)}
              style={{ ...S.btnPrimary, background: 'white', color: theme.primary, padding: '10px 20px' }}>
              ➕ Créer un devis
            </button>
          )}
          {!isAdmin && (
            <button onClick={() => navigate('/demandes')}
              style={{ ...S.btnPrimary, background: 'white', color: theme.primary, padding: '10px 20px' }}>
              ➕ Nouvelle demande
            </button>
          )}
        </div>
      </div>

      {/* ── LISTE DEVIS ──────────────────────────────────────────── */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '12px 10px' : '20px' }}>

        {/* ── Barre de filtres + recherche ─────────────────────────────── */}
        <div style={{
          display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center',
          marginBottom: '16px', background: 'white', borderRadius: '10px',
          padding: '10px 14px', boxShadow: theme.shadow,
        }}>
          {[
            { key: 'tous',      label: 'Tous' },
            { key: 'brouillon',     label: '✏️ Brouillon' },
            { key: 'envoye',        label: '📤 Envoyés (digital)' },
            { key: 'envoye_manuel', label: '📬 Envoyés (signé)' },
            { key: 'accepte',       label: '✅ Acceptés' },
            { key: 'refuse',        label: '❌ Refusés' },
            { key: 'facture',   label: '🧾 Facturés' },
            { key: 'expire',    label: '⏰ Expirés' },
          ].map(f => (
            <button key={f.key} onClick={() => setFiltreStatut(f.key)} style={{
              padding: '6px 13px', borderRadius: '20px', border: 'none', cursor: 'pointer',
              fontSize: '12px', fontWeight: '600', transition: 'all 0.15s',
              background: filtreStatut === f.key ? theme.primary : '#f0f2f5',
              color:      filtreStatut === f.key ? 'white'        : theme.textSecondary,
            }}>
              {f.label}
            </button>
          ))}
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="🔍 Client, n° devis, montant…"
            style={{
              marginLeft: isMobile ? '0' : 'auto', padding: '7px 13px',
              border: `1px solid ${theme.border}`, borderRadius: '8px',
              fontSize: '13px', width: isMobile ? '100%' : '210px',
              minWidth: isMobile ? '0' : '210px', outline: 'none', background: '#fafafa',
            }}
          />
        </div>

        {/* ── Compute filtered list ─────────────────────────────────────── */}
        {(() => {
          const q = search.toLowerCase().trim();
          const filtered = devis.filter(d => {
            const statutOK = filtreStatut === 'tous' || d.statut === filtreStatut;
            if (!q) return statutOK;
            const hay = [
              d.numero_devis,
              d.Client?.nom, d.Client?.prenom, d.Client?.email,
              d.Client?.numero_tva,
              String(d.montant_ttc || ''),
              d.statut,
              d.notes,
            ].filter(Boolean).join(' ').toLowerCase();
            return statutOK && hay.includes(q);
          });

          if (filtered.length === 0) return (
            <div style={styles.vide}>
              <p style={{ fontSize: '48px', margin: '0 0 16px' }}>📄</p>
              {search || filtreStatut !== 'tous'
                ? <p>Aucun devis ne correspond à votre recherche.</p>
                : isAdmin
                  ? <p>Aucun devis pour l'instant.<br />
                      <button onClick={() => setShowCreateModal(true)} style={{ ...S.btnPrimary, marginTop: '12px' }}>
                        Créer le premier devis
                      </button>
                    </p>
                  : <p>Vous n'avez pas encore de devis.<br />
                      <button onClick={() => navigate('/demandes')} style={{ ...S.btnPrimary, marginTop: '12px' }}>
                        Faire une demande
                      </button>
                    </p>
              }
            </div>
          );

          return filtered.map(d => {

          const verrouille = estVerrouille(d);
          return (
            <div key={d.id_devis} style={{
              background: 'white', borderRadius: '12px', marginBottom: '14px',
              boxShadow: theme.shadow, overflow: 'hidden',
              // Indication visuelle du verrouillage
              borderLeft: verrouille ? `4px solid ${theme.statut[d.statut]?.color || theme.success}` : 'none'
            }}>

              {/* En-tête */}
              <div style={{ padding: '18px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                onClick={() => setExpandedDevis(expandedDevis === d.id_devis ? null : d.id_devis)}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: '700', color: theme.primary, fontSize: '16px' }}>{d.numero_devis}</span>
                    {getStatutBadge(d.statut)}
                    {verrouille && (
                      <span style={{ fontSize: '13px', color: theme.textMuted }}>🔒 Verrouillé</span>
                    )}
                    {parseFloat(d.remise) > 0 && (
                      <span style={{ background: '#fff3e0', color: '#f39c12', padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700' }}>
                        🏷️ -{d.remise}%
                      </span>
                    )}
                    {d.version > 1 && (
                      <span style={{ background: theme.primaryBg, color: theme.primary, padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>
                        v{d.version}
                      </span>
                    )}
                    {isAdmin && d.duplique_de_id_devis && (
                      <span style={{ background: '#ede9fe', color: '#6d28d9', padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700' }}
                        title="Vérifiez les lignes, prix et notes avant l'envoi">
                        🔁 Dupliqué de {devis.find(x => x.id_devis === d.duplique_de_id_devis)?.numero_devis || `#${d.duplique_de_id_devis}`}
                      </span>
                    )}
                    {isAdmin && d.statut === 'brouillon' && d.visible_client && (
                      <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700' }}
                        title="Le client peut voir ce brouillon">
                        👁️ Visible client
                      </span>
                    )}
                  </div>
                  <div style={{ color: '#666', fontSize: '13px' }}>
                    👤 {formatNomClient(d.Client)}
                    &nbsp;|&nbsp;📅 {new Date(d.date_creation).toLocaleDateString('fr-BE')}
                    {d.date_validite && <>&nbsp;|&nbsp;Valide jusqu'au {new Date(d.date_validite).toLocaleDateString('fr-BE')}</>}
                  </div>
                  {d.adresse_chantier && (
                    <div style={{ color: '#888', fontSize: '12px', marginTop: '3px' }}>
                      📍 {d.adresse_chantier}{(d.cp_chantier || d.ville_chantier) && ', '}
                      {d.cp_chantier}{d.cp_chantier && d.ville_chantier && ' '}{d.ville_chantier}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: theme.primary }}>
                    {parseFloat(d.montant_ttc || 0).toFixed(2)} €
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {!isAdmin && d.statut === 'envoye' && (
                      <span style={{
                        background: '#ff6f00', color: 'white',
                        padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700',
                        animation: 'pulse 1.5s infinite',
                      }}>
                        ✍️ À VALIDER
                      </span>
                    )}
                    <div style={{ fontSize: '11px', color: '#999' }}>TVAC · {expandedDevis === d.id_devis ? '▲' : '▼'}</div>
                  </div>
                  {/* ── Bouton PDF client — toujours visible dans l'en-tête ── */}
                  {!isAdmin && d.statut !== 'brouillon' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); downloadDevisPDF(d.id_devis, d.numero_devis); }}
                      style={{ background: '#4f6d89', color: 'white', border: 'none', borderRadius: '6px',
                        padding: '6px 14px', fontSize: '12px', cursor: 'pointer', fontWeight: '600',
                        display: 'flex', alignItems: 'center', gap: '5px' }}
                    >
                      📄 PDF
                    </button>
                  )}
                  {/* ── Bouton VALIDER visible directement (sans expand) ── */}
                  {!isAdmin && d.statut === 'envoye' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); openAcceptModal(d); }}
                      style={{
                        background: '#e65100', color: 'white', border: 'none', borderRadius: '6px',
                        padding: '8px 16px', fontSize: '13px', cursor: 'pointer', fontWeight: '700',
                        display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap'
                      }}
                    >
                      ✅ Valider
                    </button>
                  )}
                </div>
              </div>

              {/* Détail déplié */}
              {expandedDevis === d.id_devis && (
                <div style={{ padding: isMobile ? '0 8px 12px' : '0 20px 20px', background: '#f8f9fa', borderTop: '1px solid #eee', overflowX: 'hidden' }}>

                  {/* Bannière verrouillage */}
                  {verrouille && (
                    <div style={{ margin: '16px 0 12px', padding: '10px 16px', background: '#f3e5f5', borderRadius: '8px',
                      borderLeft: `4px solid ${theme.purple}`, fontSize: '13px', color: theme.purple, fontWeight: '600' }}>
                      🔒 Ce devis est verrouillé — {d.statut === 'facture' ? 'une facture a été émise' : d.statut === 'envoye_manuel' ? 'PDF envoyé pour signature — en attente du document signé' : 'il a été accepté par le client'}.
                      Aucune modification n'est possible.
                    </div>
                  )}

                  {/* Tableau lignes */}
                  {/* ── Mobile: card view — même logique que l'IIFE desktop ─── */}
                  {isMobile && (
                    <div style={{ display:'flex', flexDirection:'column', gap:'8px', marginTop:'12px' }}>
                      {(() => {
                        // Même filtrage que l'IIFE desktop
                        const _hasOpt = (d.LigneDevis||[]).some(l => l.code_tranche && l.code_tranche !== 'TF');
                        const lignesMobile = _hasOpt
                          ? (d.LigneDevis||[]).filter(l => !l.code_tranche || l.code_tranche === 'TF')
                          : (d.LigneDevis||[]);
                        let secTotalM = 0;
                        const cards = [];
                        lignesMobile.forEach((ligne, idx) => {
                          const lid    = ligne.id_ligne_devis || ligne.id_ligne;
                          const typeLg = ligne.type_ligne || 'article';
                          // Montant calculé exactement comme le desktop
                          const montantM = (parseFloat(ligne.prix_unitaire)||0) * (parseFloat(ligne.quantite)||0);

                          if (typeLg === 'section') {
                            secTotalM = 0;
                            // Calculer total section (même logique desktop)
                            let sTotal = 0;
                            for (let j = idx+1; j < lignesMobile.length; j++) {
                              const tl = lignesMobile[j].type_ligne || 'article';
                              if (tl === 'section' || tl === 'sous_total') break;
                              sTotal += (parseFloat(lignesMobile[j].prix_unitaire)||0) * (parseFloat(lignesMobile[j].quantite)||0);
                            }
                            cards.push(
                              <div key={lid||idx} style={{ background:'#c8dff0', borderRadius:'8px',
                                padding:'10px 14px', borderLeft:'4px solid #1e3a5f',
                                display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                                <div>
                                  <div style={{ fontSize:'10px', color:'#5a7a9a', fontWeight:'700',
                                    textTransform:'uppercase', letterSpacing:'0.05em' }}>
                                    Section {ligne.numero_section||''}
                                  </div>
                                  <div style={{ fontSize:'13px', fontWeight:'900', color:'#1e3a5f', lineHeight:1.3 }}>
                                    {ligne.description_personnalisee || '—'}
                                  </div>
                                </div>
                                {sTotal > 0 && (
                                  <div style={{ fontSize:'12px', fontWeight:'700', color:'#1e3a5f',
                                    whiteSpace:'nowrap', marginLeft:'8px' }}>
                                    {sTotal.toFixed(2)} €
                                  </div>
                                )}
                              </div>
                            );
                          } else if (typeLg === 'sous_total') {
                            cards.push(
                              <div key={lid||idx} style={{ background:'#ede9fe', borderRadius:'8px',
                                padding:'8px 14px', display:'flex', justifyContent:'space-between',
                                alignItems:'center', borderLeft:'3px solid #7c3aed' }}>
                                <div style={{ fontSize:'12px', fontWeight:'700', color:'#7c3aed', fontStyle:'italic' }}>
                                  {ligne.description_personnalisee || 'Sous-total'}
                                </div>
                                <div style={{ fontSize:'13px', fontWeight:'900', color:'#7c3aed', whiteSpace:'nowrap' }}>
                                  {secTotalM.toFixed(2)} €
                                </div>
                              </div>
                            );
                            secTotalM = 0;
                          } else {
                            // Article normal
                            secTotalM += montantM;
                            const numArt = ligne.numero_section || ligne.ordre || (idx+1);
                            cards.push(
                              <div key={lid||idx} style={{ background:'white', borderRadius:'10px',
                                padding:'10px 12px', border:'1px solid #e5eaf0',
                                boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
                                {/* Numéro + Désignation */}
                                <div style={{ display:'flex', gap:'6px', marginBottom:'8px', alignItems:'flex-start' }}>
                                  <span style={{ fontSize:'10px', fontWeight:'700', color:'#9ca3af',
                                    background:'#f1f5f9', borderRadius:'4px', padding:'2px 6px',
                                    flexShrink:0, marginTop:'1px' }}>{numArt}</span>
                                  <div style={{ fontSize:'13px', fontWeight:'600', color:'#1e293b', lineHeight:1.4, flex:1 }}>
                                    {ligne.description_personnalisee || '—'}
                                  </div>
                                </div>
                                {/* Grille Qté | P.U. HT | Total HT */}
                                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'5px' }}>
                                  <div style={{ background:'#f8fafc', borderRadius:'6px', padding:'5px 8px', textAlign:'center' }}>
                                    <div style={{ color:'#94a3b8', fontSize:'9px', fontWeight:'700',
                                      textTransform:'uppercase', marginBottom:'2px' }}>Qté</div>
                                    <div style={{ fontWeight:'700', fontSize:'12px', color:'#1e293b' }}>
                                      {ligne.quantite}{ligne.unite ? ' '+ligne.unite : ''}
                                    </div>
                                  </div>
                                  <div style={{ background:'#f8fafc', borderRadius:'6px', padding:'5px 8px', textAlign:'center' }}>
                                    <div style={{ color:'#94a3b8', fontSize:'9px', fontWeight:'700',
                                      textTransform:'uppercase', marginBottom:'2px' }}>P.U. HT</div>
                                    <div style={{ fontWeight:'700', fontSize:'12px', color:'#1e293b' }}>
                                      {(parseFloat(ligne.prix_unitaire)||0).toFixed(2)} €
                                    </div>
                                  </div>
                                  <div style={{ background:'#EFF6FF', borderRadius:'6px', padding:'5px 8px', textAlign:'center',
                                    border:'1px solid #bfdbfe' }}>
                                    <div style={{ color:'#1d4ed8', fontSize:'9px', fontWeight:'700',
                                      textTransform:'uppercase', marginBottom:'2px' }}>Total HT</div>
                                    <div style={{ fontWeight:'800', fontSize:'12px', color:'#1d4ed8' }}>
                                      {montantM.toFixed(2)} €
                                    </div>
                                  </div>
                                </div>
                                {/* Remise ligne */}
                                {parseFloat(ligne.remise_ligne||0) > 0 && (
                                  <div style={{ marginTop:'5px', fontSize:'11px', color:'#f59e0b',
                                    fontWeight:'600', display:'flex', alignItems:'center', gap:'4px' }}>
                                    🏷️ Remise ligne: -{ligne.remise_ligne}%
                                  </div>
                                )}
                                {/* TVA */}
                                <div style={{ marginTop:'4px', fontSize:'10px', color:'#94a3b8' }}>
                                  TVA {ligne.tva||0}%
                                  {ligne.code_tranche && ligne.code_tranche !== 'TF' && (
                                    <span style={{ marginLeft:'8px', background:'#fef3c7', color:'#92400e',
                                      padding:'1px 6px', borderRadius:'4px', fontWeight:'700' }}>
                                      {ligne.code_tranche}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          }
                        });
                        return cards;
                      })()}
                    </div>
                  )}
                  {/* ── Desktop: table classique ──────────────────────────── */}
                  {!isMobile && <div style={{ overflowX: 'auto', marginTop: '12px' }}>
                    <table style={{ width: '100%', minWidth: isMobile ? '550px' : '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '8px', minWidth: '560px', overflow: 'hidden' }}>
                      <thead>
                        <tr style={{ background: theme.primary, color: 'white' }}>
                          <th style={TH}>#</th>
                          <th style={{ ...TH, textAlign: 'left' }}>Désignation</th>
                          <th style={TH}>Unité</th>
                          <th style={TH}>P.U. HT</th>
                          <th style={TH}>Qté</th>
                          {!isMobile && <th style={TH}>TVA%</th>}
                          <th style={TH}>Total HT</th>
                          {isAdmin && !verrouille && <th style={TH}>Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          // Calcul des sous-totaux par section pour l'affichage
                          let secTotal = 0;
                          const rows = [];
                          // Afficher uniquement les lignes TF dans le tableau principal
                          // Les lignes TA/TB sont affichées dans TranchesPanel
                          // Détecter les tranches optionnelles depuis les lignes (fiable sans has_tranches du modèle)
                          const _hasOpt = (d.LigneDevis||[]).some(l => l.code_tranche && l.code_tranche !== 'TF');
                          const lignes  = _hasOpt
                            ? (d.LigneDevis || []).filter(l => !l.code_tranche || l.code_tranche === 'TF')
                            : (d.LigneDevis || []);
                          lignes.forEach((ligne, idx) => {
                            const lid      = ligne.id_ligne_devis || ligne.id_ligne;
                            const isEdit   = editingLigne === lid && !verrouille;
                            const typeLg   = ligne.type_ligne || 'article';
                            const montant  = (parseFloat(ligne.prix_unitaire)||0) * (parseFloat(ligne.quantite)||0);

                            if (typeLg === 'section') {
                              secTotal = 0; // reset
                              // Calculer le total de cette section (articles suivants jusqu'au prochain sous_total/section)
                              let sTotal = 0;
                              for (let j = idx+1; j < lignes.length; j++) {
                                const tl = lignes[j].type_ligne || 'article';
                                if (tl === 'section' || tl === 'sous_total') break;
                                sTotal += (parseFloat(lignes[j].prix_unitaire)||0) * (parseFloat(lignes[j].quantite)||0);
                              }
                              rows.push(
                                <tr key={lid || idx} style={{ background: '#c8dff0', borderBottom: '2px solid #7aabcd', borderTop: '1px solid #7aabcd' }}>
                                  <td style={{ ...TD, color: '#1e3a5f', fontWeight: '900', fontSize: '12px' }}>
                                    {ligne.numero_section || ''}
                                  </td>
                                  <td colSpan={5} style={{ ...TD, color: '#1e3a5f', fontWeight: '900', fontSize: '12px', textTransform: 'uppercase' }}>
                                    {editingLigne === lid
                                      ? <input type="text" value={ligne.description_personnalisee || ''} style={{ ...S.inlineInput, fontWeight: 700 }}
                                          onChange={e => handleFieldChange(d.id_devis, lid, 'description_personnalisee', e.target.value)}
                                          placeholder="Titre de la section…" />
                                      : ligne.description_personnalisee
                                    }
                                  </td>
                                  <td style={{ ...TD, textAlign: 'right', fontWeight: '700', color: '#1e3a5f' }}>
                                    {sTotal > 0 ? `${sTotal.toFixed(2)} €` : ''}
                                  </td>
                                  {isAdmin && !verrouille && (
                                    <td style={{ ...TD, textAlign: 'center' }}>
                                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                                        {editingLigne === lid
                                          ? <button onClick={() => saveLigneUpdate(ligne, d.id_devis)} style={{ ...S.btnSm, background: theme.success }}>✅</button>
                                          : <button onClick={() => setEditingLigne(lid)} style={{ ...S.btnSm, background: theme.primary }} title="Renommer la section">✏️</button>
                                        }
                                        <button onClick={() => handleDeleteLigne(lid, d.id_devis)} style={{ ...S.btnSm, background: theme.danger }}>🗑️</button>
                                      </div>
                                    </td>
                                  )}
                                </tr>
                              );
                            } else if (typeLg === 'sous_total') {
                              rows.push(
                                <tr key={lid || idx} style={{ background: '#ede9fe', borderBottom: '1px solid #ddd6fe' }}>
                                  <td style={TD}></td>
                                  <td colSpan={4} style={{ ...TD, color: '#7c3aed', fontWeight: '700', fontStyle: 'italic', textAlign: 'right' }}>
                                    {ligne.description_personnalisee || 'Sous-total'}
                                  </td>
                                  <td style={{ ...TD, textAlign: 'right', fontWeight: '900', color: '#7c3aed' }}>
                                    {secTotal.toFixed(2)} €
                                  </td>
                                  {isAdmin && !verrouille && (
                                    <td style={{ ...TD, textAlign: 'center' }}>
                                      <button onClick={() => handleDeleteLigne(lid, d.id_devis)} style={{ ...S.btnSm, background: theme.danger }}>🗑️</button>
                                    </td>
                                  )}
                                </tr>
                              );
                              secTotal = 0; // reset après sous-total affiché
                            } else {
                              // Article normal
                              secTotal += montant;
                              rows.push(
                                <tr key={lid || idx} style={{ borderBottom: '1px solid #eee', background: idx % 2 === 0 ? '#f9fafb' : 'white' }}>
                                  <td style={{ ...TD, color: '#999', fontWeight: '600' }}>
                                    {ligne.numero_section || ligne.ordre || idx + 1}
                                  </td>
                                  <td style={TD}>
                                     {/* Badge / sélecteur tranche (si devis avec tranches) */}
                                     {d.has_tranches && (() => {
                                       // TF + codes optionnels depuis les lignes existantes
                                       const usedCodes = [...new Set((d.LigneDevis||[])
                                         .map(l=>l.code_tranche).filter(c=>c&&c!=='TF'))];
                                       const activeTranches = ['TF',...usedCodes,
                                         ...['TA','TB','TC','TD','TE'].filter(c=>!usedCodes.includes(c) && d.has_tranches)
                                       ];
                                       return isEdit ? (
                                         <select value={ligne.code_tranche||'TF'}
                                           onChange={e=>handleFieldChange(d.id_devis,lid,'code_tranche',e.target.value)}
                                           style={{fontSize:'9px',padding:'1px 4px',borderRadius:'3px',marginBottom:'2px',
                                             border:'1px solid #cbd5e1',display:'block',
                                             background:ligne.code_tranche&&ligne.code_tranche!=='TF'?'#fef3c7':'#f0fdf4',
                                             color:ligne.code_tranche&&ligne.code_tranche!=='TF'?'#92400e':'#16a34a',fontWeight:'700'}}>
                                           {activeTranches.map(c=><option key={c} value={c}>{c==='TF'?'Ferme':c+' Opt.'}</option>)}
                                         </select>
                                       ) : ligne.code_tranche && ligne.code_tranche !== 'TF' ? (
                                         <span style={{fontSize:'9px',fontWeight:'700',padding:'1px 5px',borderRadius:'3px',
                                           background:'#fef3c7',color:'#92400e',display:'inline-block',marginBottom:'2px'}}>
                                           {ligne.code_tranche}
                                         </span>
                                       ) : null;
                                     })()}
                                     {isEdit
                                       ? <div style={{ display:'flex', flexDirection:'column', gap:'3px' }}>
                                            <div style={{ display:'flex', gap:'4px', alignItems:'center', marginBottom:'2px' }}>
                                              {[{m:'**',bl:'B',t:'Gras (**texte**)',bs:{fontWeight:'900'}},
                                                {m:'_', bl:'I',t:'Italique (_texte_)',bs:{fontStyle:'italic',fontWeight:'600'}},
                                                {m:'__',bl:'U',t:'Souligné (__texte__)',bs:{textDecoration:'underline',fontWeight:'600'}}]
                                               .map(({m,bl,t,bs}) => (
                                                <button key={bl} type="button" title={t}
                                                  onMouseDown={e => {
                                                    e.preventDefault();
                                                    const ta = document.querySelector('[data-lid="' + lid + '"]');
                                                    if (!ta) return;
                                                    const sv = ta.selectionStart, ev = ta.selectionEnd;
                                                    const val = ligne.description_personnalisee || '';
                                                    const sel = val.substring(sv, ev);
                                                    const ins = sel ? m+sel+m : m+(bl==='B'?'gras':bl==='I'?'italique':'souligné')+m;
                                                    handleFieldChange(d.id_devis, lid, 'description_personnalisee',
                                                      val.substring(0,sv)+ins+val.substring(ev));
                                                  }}
                                                  style={{ padding:'2px 8px', borderRadius:'4px', border:'1px solid #cbd5e1',
                                                    background:'white', cursor:'pointer', fontSize:'12px',
                                                    lineHeight:1, color:'#1e3a5f', ...bs }}>
                                                  {bl}
                                                </button>
                                              ))}
                                              <span style={{ fontSize:'9px', color:'#9ca3af', alignSelf:'center' }}>
                                                **gras** _ital_ __souligné__ · ↵
                                              </span>
                                            </div>
                                           <textarea
                                             data-lid={lid}
                                             value={ligne.description_personnalisee || ''}
                                             onChange={e => handleFieldChange(d.id_devis, lid, 'description_personnalisee', e.target.value)}
                                             rows={3}
                                             style={{ ...S.inlineInput, resize:'vertical', minHeight:'52px',
                                               fontFamily:'inherit', lineHeight:1.4, whiteSpace:'pre-wrap' }}
                                           />
                                         </div>
                                       : <div style={{ whiteSpace:'pre-wrap', lineHeight:1.5 }}>
                                           {renderRichText(ligne.description_personnalisee)}
                                         </div>
                                     }
                                  </td>
                                  <td style={{ ...TD, textAlign: 'center' }}>
                                    {isEdit
                                      ? <select value={ligne.unite || ''} style={{ ...S.inlineInput, width: '60px' }}
                                          onChange={e => handleFieldChange(d.id_devis, lid, 'unite', e.target.value)}>
                                          {['','h','FF','stuk','m²','ml','m','kg','kW','kWh','jr','pcs'].map(u =>
                                            <option key={u} value={u}>{u || '—'}</option>
                                          )}
                                        </select>
                                      : (ligne.unite || '—')
                                    }
                                  </td>
                                  <td style={{ ...TD, textAlign: 'right' }}>
                                    {isEdit
                                      ? <input type="number" value={ligne.prix_unitaire} style={{ ...S.inlineInput, width: '80px', textAlign: 'right' }}
                                          onChange={e => handleFieldChange(d.id_devis, lid, 'prix_unitaire', e.target.value)} />
                                      : `${parseFloat(ligne.prix_unitaire || 0).toFixed(2)} €`
                                    }
                                  </td>
                                  <td style={{ ...TD, textAlign: 'center' }}>
                                    {isEdit
                                      ? <input type="number" value={ligne.quantite} style={{ ...S.inlineInput, width: '50px', textAlign: 'center' }}
                                          onChange={e => handleFieldChange(d.id_devis, lid, 'quantite', e.target.value)} />
                                      : ligne.quantite
                                    }
                                  </td>
                                   {!isMobile && (
                                     <td style={{ ...TD, textAlign: 'center' }}>
                                       {isEdit
                                         ? <select
                                             value={parseFloat(ligne.tva) || 0}
                                             onChange={e => handleFieldChange(d.id_devis, lid, 'tva', parseFloat(e.target.value))}
                                             style={{ ...S.inlineInput, width: '58px', textAlign: 'center', padding: '3px 4px' }}>
                                             {[0, 6, 12, 21].map(v => (
                                               <option key={v} value={v}>{v}%</option>
                                             ))}
                                           </select>
                                         : `${parseFloat(ligne.tva) || 0}%`
                                       }
                                     </td>
                                   )}
                                  <td style={{ ...TD, textAlign: 'right', fontWeight: '600' }}>
                                    {montant.toFixed(2)} €
                                  </td>
                                  {isAdmin && !verrouille && (
                                    <td style={{ ...TD, textAlign: 'center' }}>
                                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                        {isEdit
                                          ? <button onClick={() => saveLigneUpdate(ligne, d.id_devis)} style={{ ...S.btnSm, background: theme.success }}>✅</button>
                                          : <button onClick={() => setEditingLigne(lid)} style={{ ...S.btnSm, background: theme.primary }}>✏️</button>
                                        }
                                        <button onClick={() => handleDeleteLigne(lid, d.id_devis)} style={{ ...S.btnSm, background: theme.danger }}>🗑️</button>
                                      </div>
                                    </td>
                                  )}
                                </tr>
                              );
                            }
                          });
                          return rows;
                        })()}
                      </tbody>
                    </table>
                  </div>}

                  {/* Ajouter ligne — admin, non verrouillé */}
                  {isAdmin && !verrouille && (
                    <InsertLigneBar
                      devis={d}
                      token={token}
                      apiUrl={API_URL}
                      onRefresh={fetchDevis}
                      onAddFin={() => handleAddLigneLibre(d)}
                    />
                  )}

                  {/* Totaux */}
                  <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                    <table style={{ borderCollapse: 'collapse', minWidth: '280px' }}>
                      <tbody>
                        <tr>
                          <td style={{ padding: '6px 12px', color: '#666', textAlign: 'right' }}>Sous-total HTVA :</td>
                          <td style={{ padding: '6px 12px', textAlign: 'right', fontWeight: '500' }}>{parseFloat(d.montant_ht || 0).toFixed(2)} €</td>
                        </tr>
                        {parseFloat(d.remise) > 0 && (
                          <tr style={{ color: '#e67e22' }}>
                            <td style={{ padding: '6px 12px', textAlign: 'right' }}>Remise ({d.remise}%) :</td>
                            <td style={{ padding: '6px 12px', textAlign: 'right', fontWeight: '600' }}>
                              -{(parseFloat(d.montant_ht) * parseFloat(d.remise) / 100).toFixed(2)} €
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td style={{ padding: '6px 12px', color: '#666', textAlign: 'right' }}>TVA :</td>
                          <td style={{ padding: '6px 12px', textAlign: 'right' }}>{parseFloat(d.montant_tva || 0).toFixed(2)} €</td>
                        </tr>
                        <tr style={{ background: theme.primary, color: 'white', fontWeight: '700', fontSize: '15px' }}>
                          <td style={{ padding: '10px 12px', textAlign: 'right', borderRadius: '6px 0 0 6px' }}>TOTAL TVAC :</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', borderRadius: '0 6px 6px 0' }}>{parseFloat(d.montant_ttc || 0).toFixed(2)} €</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* ── CONFIGURATION ÉCHÉANCIER (admin, avant envoi/acceptation) ── */}
                  {isAdmin && (
                    <div style={{ marginTop:'16px', padding:'14px', background:'#f0f4f8', borderRadius:'10px', border:'1px solid #d0dce8' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: editEchDevis===d.id_devis?'14px':'0' }}>
                        <div>
                          <span style={{ fontWeight:'700', fontSize:'13px', color:'#2c3e50' }}>📋 Échéancier de facturation</span>
                          {d.config_echeancier && Array.isArray(d.config_echeancier) && d.config_echeancier.length > 0 ? (
                            <span style={{ marginLeft:'10px', fontSize:'12px', color:'#27ae60', fontWeight:'600' }}>
                              {d.config_echeancier.length} palier{d.config_echeancier.length>1?'s':''} — {d.config_echeancier.map(p=>`${p.label} ${p.pct}%`).join(' → ')}
                            </span>
                          ) : (
                            <span style={{ marginLeft:'10px', fontSize:'12px', color:'#e67e22' }}>Non configuré (à définir avant envoi)</span>
                          )}
                        </div>
                        <button
                          onClick={() => editEchDevis===d.id_devis ? setEditEchDevis(null) : ouvrirEditEcheancier(d)}
                          style={{ padding:'5px 14px', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'600',
                            background: editEchDevis===d.id_devis ? '#e0e0e0' : '#4f6d89', color: editEchDevis===d.id_devis?'#333':'white' }}>
                          {editEchDevis===d.id_devis ? 'Annuler' : '✏️ Modifier'}
                        </button>
                      </div>

                      {editEchDevis === d.id_devis && (
                        <div>
                          {/* Sélecteur nombre de paliers */}
                          <div style={{ marginBottom:'10px' }}>
                            <div style={{ fontSize:'11px',fontWeight:'600',color:'#555',marginBottom:'6px' }}>Nombre de paliers</div>
                            <div style={{ display:'flex',gap:'6px' }}>
                              {[1,2,3,4,5].map(n => (
                                <button key={n} type="button"
                                  onClick={() => setEditEchPaliers(ECH_TEMPLATES[n])}
                                  style={{ padding:'5px 13px',borderRadius:'6px',border:'none',cursor:'pointer',fontWeight:'600',fontSize:'12px',
                                    background:editEchPaliers.length===n?'#4f6d89':'#e8edf2',
                                    color:editEchPaliers.length===n?'white':'#444' }}>
                                  {n}
                                </button>
                              ))}
                            </div>
                            <div style={{ fontSize:'11px',color:'#888',marginTop:'4px' }}>
                              {editEchPaliers.length===1 ? "⚡ Facture complète (100% en une fois)" : "Chaque palier déclenche une facture séparée"}
                            </div>
                          </div>
                          {/* Grille paliers */}
                          <div style={{ background:'white',borderRadius:'8px',padding:'10px',border:'1px solid #dce8f0' }}>
                            <div style={{ display:'grid',gridTemplateColumns:'2fr 3fr 1fr',gap:'6px',marginBottom:'8px' }}>
                              <span style={{ fontSize:'11px',color:'#888',fontWeight:'600' }}>PALIER</span>
                              <span style={{ fontSize:'11px',color:'#888',fontWeight:'600' }}>LABEL</span>
                              <span style={{ fontSize:'11px',color:'#888',fontWeight:'600',textAlign:'right' }}>%</span>
                            </div>
                            {editEchPaliers.map((pal,idx) => (
                              <div key={idx} style={{ display:'grid',gridTemplateColumns:'2fr 3fr 1fr',gap:'6px',marginBottom:'6px',alignItems:'center' }}>
                                <div style={{ padding:'4px 8px',borderRadius:'4px',fontSize:'12px',fontWeight:'700',textAlign:'center',
                                  background:idx===0?'#fff3e0':idx===editEchPaliers.length-1?'#e8f5e9':'#e3f2fd',
                                  color:idx===0?'#e65100':idx===editEchPaliers.length-1?'#1b5e20':'#1565c0' }}>
                                  {idx===0&&editEchPaliers.length>1?`${idx+1}. Acompte`:idx===editEchPaliers.length-1&&editEchPaliers.length>1?`${idx+1}. Solde`:`${idx+1}.`}
                                </div>
                                <input type="text" value={pal.label}
                                  onChange={e=>{const p=[...editEchPaliers];p[idx]={...p[idx],label:e.target.value};setEditEchPaliers(p);}}
                                  style={{ border:'1px solid #ddd',borderRadius:'6px',padding:'6px 8px',fontSize:'13px',width:'100%',boxSizing:'border-box' }}
                                />
                                <input type="number" min="1" max="100" value={pal.pct}
                                  onChange={e=>{const p=[...editEchPaliers];p[idx]={...p[idx],pct:parseFloat(e.target.value)||0};setEditEchPaliers(p);}}
                                  style={{ border:'1px solid #ddd',borderRadius:'6px',padding:'6px',fontSize:'13px',textAlign:'right',width:'100%',boxSizing:'border-box' }}
                                />
                              </div>
                            ))}
                            {(() => {
                              const somme=editEchPaliers.reduce((s,p)=>s+(parseFloat(p.pct)||0),0);
                              const ok=Math.abs(somme-100)<0.01;
                              return (
                                <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:'8px',paddingTop:'8px',borderTop:'1px solid #eee' }}>
                                  <span style={{ fontSize:'12px',color:'#666' }}>Somme :</span>
                                  <span style={{ fontWeight:'700',fontSize:'13px',color:ok?'#27ae60':'#e74c3c',background:ok?'#e8f5e9':'#fdecea',padding:'2px 10px',borderRadius:'10px' }}>
                                    {somme.toFixed(0)}% {ok?'✓ OK':'⚠️ doit être 100%'}
                                  </span>
                                </div>
                              );
                            })()}
                          </div>
                          <button onClick={()=>sauvegarderEcheancierDevis(d.id_devis)} disabled={editEchLoading}
                            style={{ marginTop:'10px',width:'100%',padding:'9px',border:'none',borderRadius:'8px',background:'#27ae60',color:'white',fontWeight:'700',fontSize:'13px',cursor:'pointer',opacity:editEchLoading?0.7:1 }}>
                            {editEchLoading?'⏳ Sauvegarde…':"💾 Sauvegarder l'échéancier"}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── ACTIONS ADMIN (masquées si verrouillé) ── */}
                  {isAdmin && (
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #eee' }}>
                      {/* Dupliquer — toujours disponible, quel que soit le statut */}
                      <button
                        onClick={(e) => { e.stopPropagation(); dupliquerDevis(d); }}
                        style={{ ...S.btnPrimary, background: '#6d28d9' }}
                        title="Créer un nouveau devis à partir de celui-ci (client, lignes et prix modifiables)">
                        🔁 Dupliquer
                      </button>
                      {!verrouille && (
                        <>
                          <button onClick={() => { setTVAValue(d.taux_tva || 21); setTvaRegimes(d.tva_regimes || {}); setShowTVAModal(d.id_devis); }} style={S.btnPrimary}>⚡ TVA</button>
                          <button onClick={() => setShowRemiseModal(d.id_devis)} style={{ ...S.btnPrimary, background: '#f39c12' }}>🏷️ Remise</button>
                          {d.statut === 'brouillon' && (
                            <button
                              onClick={() => toggleVisibiliteClient(d)}
                              disabled={visibiliteLoading === d.id_devis}
                              style={{ ...S.btnPrimary, background: d.visible_client ? '#0369a1' : '#94a3b8',
                                opacity: visibiliteLoading === d.id_devis ? 0.6 : 1 }}
                              title={d.visible_client
                                ? "Le client voit ce brouillon — cliquez pour le masquer à nouveau"
                                : "Ce brouillon est invisible pour le client tant que vous ne l'envoyez pas — cliquez pour le lui montrer volontairement"}>
                              {visibiliteLoading === d.id_devis
                                ? '⏳…'
                                : d.visible_client ? '🙈 Masquer au client' : '👁️ Rendre visible au client'}
                            </button>
                          )}
                          {['brouillon', 'envoye'].includes(d.statut) && (
                            isRealEmail(d.Client?.email)
                              ? (
                                <button onClick={() => openEnvoiModal(d)}
                                  style={{ ...S.btnPrimary, background: theme.info }}
                                  title={d.statut === 'envoye' ? `Renvoyer le devis modifié à ${d.Client?.email}` : `Envoyer le devis par email à ${d.Client?.email}`}>
                                  {d.statut === 'envoye' ? '🔄 Renvoyer + PDF' : '📤 Envoyer + PDF'}
                                </button>
                              ) : (
                                <>
                                  <button disabled
                                    style={{ ...S.btnPrimary, background: '#94a3b8',
                                      cursor: 'not-allowed', opacity: 0.6 }}
                                    title="Ce client n'a pas d'email — utilisez Imprimer / Télécharger">
                                    📤 Envoyer + PDF
                                  </button>
                                  <button
                                    onClick={() => downloadDevisPDF(d.id_devis, d.numero_devis)}
                                    style={{ ...S.btnPrimary, background: '#0891b2', fontSize: '12px' }}
                                    title="Télécharger le PDF pour remise en main propre ou impression">
                                    🖨️ Imprimer / PDF
                                  </button>
                                </>
                              )
                          )}
                        </>
                      )}
                      {/* Bouton PDF — toujours visible pour admin, même brouillon */}
                      <button
                        onClick={() => downloadDevisPDF(d.id_devis, d.numero_devis)}
                        style={{ ...S.btnPrimary, background: d.statut === 'brouillon' ? '#94a3b8' : '#7f8c8d' }}
                        title={d.statut === 'brouillon' ? 'PDF v0 (brouillon non envoyé)' : 'Télécharger le PDF'}
                      >
                        📄 {d.statut === 'brouillon' ? 'PDF v0' : 'PDF'}
                      </button>
                      {/* Envoyer PDF pour signature manuelle (même en brouillon) */}
                      {!['accepte','facture','facture_partielle'].includes(d.statut) && (
                        <button
                          onClick={() => envoyerManuel(d.id_devis, d.numero_devis, d.Client?.email)}
                          style={{ ...S.btnPrimary, background: '#0891b2', fontSize: '12px' }}
                          title={isRealEmail(d.Client?.email)
                            ? `Envoyer PDF à ${d.Client.email} pour signature manuelle`
                            : 'Télécharger PDF (client sans email)'}
                        >
                          {isRealEmail(d.Client?.email) ? '✉️ Envoyer signé' : '📥 Télécharger'}
                        </button>
                      )}
                      {/* Marquer accepté manuellement quand document signé reçu */}
                      {d.statut === 'envoye_manuel' && (
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Confirmer la réception du devis ${d.numero_devis} signé par le client ?`)) return;
                            try {
                              await axios.patch(`${API_URL}/devis/${d.id_devis}/statut`,
                                { statut: 'accepte' }, config);
                              await fetchDevis();
                              alert('✅ Devis marqué comme accepté. Vous pouvez maintenant émettre les factures.');
                            } catch(e) { alert('Erreur: ' + e.message); }
                          }}
                          style={{ ...S.btnPrimary, background: '#16a34a', fontSize: '12px' }}
                          title="Confirmer réception du devis signé — permet d'émettre les factures"
                        >
                          ✅ Doc signé reçu
                        </button>
                      )}
                      {/* Preuve d'acceptation — uniquement si devis accepté */}
                      {(d.statut === 'accepte' || d.statut === 'facture' || d.statut === 'facture_partielle') && (
                        <>
                          {(d.statut === 'accepte' || d.statut === 'facture' || d.statut === 'facture_partielle') && (
                            <button
                              onClick={() => downloadPreuve(d.id_devis, d.numero_devis, d.date_acceptation)}
                              title={`Preuve juridique — accepté le ${d.date_acceptation ? new Date(d.date_acceptation).toLocaleDateString('fr-BE') : '?'}`}
                              style={{ ...S.btnPrimary, background: '#2e7d32', fontSize: '12px' }}>
                              📋 Preuve
                            </button>
                          )}
                          {/* Débloquer après NC — uniquement si devis verrouillé par facturation */}
                          {['facture','facture_partielle'].includes(d.statut) && (
                            <button
                              onClick={() => debloquerDevis(d)}
                              style={{ ...S.btnPrimary, background: '#c62828', fontSize: '12px' }}
                              title="Débloquer ce devis après émission d'une note de crédit — permet de re-facturer avec TVA corrigée">
                              🔓 Débloquer
                            </button>
                          )}
                          {/* Après déblocage : invitation à facturer visible immédiatement */}
                          {d.statut === 'accepte' && pendingRefs && (
                            <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7',
                              borderRadius: '6px', padding: '6px 10px', fontSize: '11px', color: '#2e7d32' }}>
                              ✅ Débloqué — cliquez <strong>Facturer</strong> ci-dessous ↓
                            </div>
                          )}
                          <button
                            onClick={() => creerAvenant(d)}
                            title="Créer un avenant (travaux supplémentaires, modification)"
                            style={{ ...S.btnPrimary, background: '#6a1b9a', fontSize: '12px' }}>
                            📝 Avenant
                          </button>
                        </>
                      )}

                      {/* ── AVENANTS LIÉS ── affichage si ce devis a des avenants enfants ── */}
                      {d.Avenants && d.Avenants.length > 0 && (
                        <div style={{ margin: '10px 0 4px', padding: '10px 14px',
                          background: '#f3e5f5', borderRadius: '8px', borderLeft: '3px solid #7b1fa2' }}>
                          <div style={{ fontWeight: '600', fontSize: '12px', color: '#6a1b9a', marginBottom: '6px' }}>
                            📝 {d.Avenants.length} avenant(s) lié(s)
                          </div>
                          {d.Avenants.map(av => (
                            <div key={av.id_devis} style={{ display: 'flex', alignItems: 'center',
                              gap: '10px', fontSize: '12px', padding: '4px 0',
                              borderTop: '1px solid rgba(123,31,162,0.15)' }}>
                              <span style={{ color: '#6a1b9a', fontWeight: '600' }}>{av.numero_devis}</span>
                              <span style={{ color: '#666' }}>{av.statut}</span>
                              <span style={{ color: '#666' }}>
                                {parseFloat(av.montant_ttc || 0).toFixed(2)} €
                              </span>
                              <span style={{ color: '#999', fontSize: '11px' }}>
                                {av.notes}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                                            {/* ── ÉCHÉANCIER FLEXIBLE ── */}
                      {(d.statut === 'accepte' || d.statut === 'facture') && (() => {
                        const factures = d.Factures || [];
                        const hasEch = (n) => factures.some(f => f.numero_echeance === n);

                        // Lire les paliers depuis config_echeancier (JSONB) ou utiliser 40/50/10 par défaut
                        const ECHA = (d.config_echeancier && Array.isArray(d.config_echeancier) && d.config_echeancier.length > 0)
                          ? d.config_echeancier.map((p, i, arr) => ({
                              ...p,
                              emoji: i === 0 ? '💶' : i === arr.length - 1 ? '✅' : '📈',
                            }))
                          : [
                              { num: 1, label: 'Acompte',    pct: 40, emoji: '💶' },
                              { num: 2, label: 'Avancement', pct: 50, emoji: '📈' },
                              { num: 3, label: 'Solde',       pct: 10, emoji: '✅' },
                            ];
                        const nbPaliers = ECHA.length;
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%', marginTop: '4px' }}>
                            {/* Barre de progression */}
                            <div style={{ display: 'flex', gap: '4px', marginBottom: '2px' }}>
                              {ECHA.map(e => {
                                const done = hasEch(e.num);
                                return (
                                  <div key={e.num} style={{
                                    flex: e.pct, height: '6px', borderRadius: '3px',
                                    background: done ? '#27ae60' : '#dce8f0',
                                    transition: 'background 0.3s'
                                  }} title={`${e.label} ${e.pct}% — ${done ? 'facturé' : 'en attente'}`} />
                                );
                              })}
                            </div>
                            {/* Boutons */}
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              {/* Titre de la section */}
                              <div style={{ width: '100%', fontSize: '11px', color: '#666', marginBottom: '2px' }}>
                                📋 Échéancier {nbPaliers} palier{nbPaliers > 1 ? 's' : ''} :
                                {ECHA.map(e => ` ${e.label} ${e.pct}%`).join(' →')}
                              </div>
                              {ECHA.map(e => {
                                const done   = hasEch(e.num);
                                // Verrouillé si le palier précédent n'a pas été facturé
                                const locked = e.num > 1 && !hasEch(e.num - 1);
                                const btnStyle = done
                                  ? { ...S.btnPrimary, background: '#27ae60', cursor: 'default', opacity: 0.75 }
                                  : locked
                                  ? { ...S.btnPrimary, background: '#95a5a6', cursor: 'not-allowed', opacity: 0.6 }
                                  : { ...S.btnPrimary, background: '#4f6d89' };
                                const prevPalier = ECHA.find(p => p.num === e.num - 1);
                                return (
                                  <button
                                    key={e.num}
                                    disabled={done || locked}
                                    onClick={() => !done && !locked && factureEcheance(d.id_devis, e.num, `${e.label} ${e.pct}%`)}
                                    style={btnStyle}
                                    title={done ? 'Déjà facturé' : locked ? `Attendez la facture "${prevPalier?.label}"` : `Émettre facture ${e.label} (${e.pct}%)`}
                                  >
                                    {e.emoji} {done ? `${e.label} ✓` : `Facturer ${e.label} (${e.pct}%)`}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                      {!verrouille && (
                        <button onClick={() => handleDelete(d.id_devis, d.statut)} style={{ ...S.btnPrimary, background: theme.danger }}>
                          🗑️ Supprimer
                        </button>
                      )}
                    </div>
                  )}

                  {/* ── BOUTON PDF CLIENT — visible dès que le devis est envoyé ── */}
                  {!isAdmin && d.statut !== 'brouillon' && (
                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #eee' }}>
                      <button
                        onClick={() => downloadDevisPDF(d.id_devis, d.numero_devis)}
                        style={{ ...S.btnPrimary, background: '#4f6d89', padding: '10px 20px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}
                      >
                        📄 Télécharger le devis PDF
                      </button>
                    </div>
                  )}

                  {/* ── ACTIONS CLIENT (accepter/refuser si envoyé) ── */}
                  {!isAdmin && d.statut === 'envoye' && (
                    <div style={{ marginTop: '12px' }}>

                      {/* Note légale dynamique */}
                      <div style={{
                        background: '#fff8e1', border: '1px solid #f9a825',
                        borderRadius: '8px', padding: '14px 16px', marginBottom: '12px',
                        fontSize: '13px', lineHeight: '1.6',
                      }}>
                        <div style={{ fontWeight: '700', color: '#e65100', marginBottom: '8px', fontSize: '13px' }}>
                          ⚖️ Engagement contractuel électronique
                        </div>
                        <p style={{ margin: '0 0 6px' }}>
                          En cliquant sur <strong>Accepter</strong>, vous confirmez votre acceptation
                          du devis <strong>{d.numero_devis}</strong> à :
                        </p>
                        <div style={{
                          background: '#fff', border: '1px solid #ffe082', borderRadius: '6px',
                          padding: '8px 12px', fontFamily: 'monospace', fontSize: '12px', color: '#333',
                          marginBottom: '8px',
                        }}>
                          📅 {new Date().toLocaleString('fr-BE', {
                            timeZone: 'Europe/Brussels',
                            dateStyle: 'full', timeStyle: 'medium'
                          })}
                        </div>
                        <p style={{ margin: '0', fontSize: '12px', color: '#666' }}>
                          Cette acceptation vaut signature manuscrite et engagement contractuel ferme,
                          conformément à la loi belge du 21/07/2016. Votre adresse IP et
                          l'horodatage exact seront enregistrés à des fins probatoires.
                        </p>
                      </div>

                      {/* Checkbox CGV obligatoire */}
                      <label style={{
                        display: 'flex', alignItems: 'flex-start', gap: '10px',
                        background: '#e8f5e9', border: '1px solid #a5d6a7',
                        borderRadius: '8px', padding: '12px 14px', marginBottom: '12px',
                        cursor: 'pointer', fontSize: '13px',
                      }}>
                        <input
                          type="checkbox"
                          checked={showAcceptModal?.id_devis === d.id_devis
                            ? acceptCGV
                            : false}
                          onChange={e => {
                            setShowAcceptModal(d);
                            setAcceptCGV(e.target.checked);
                          }}
                          style={{ marginTop: '2px', width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        <span>
                          J'ai lu et j'accepte les <strong>Conditions Générales de Vente</strong> (jointes
                          en page 2 du devis PDF). Cette case doit être cochée pour valider votre acceptation.
                        </span>
                      </label>

                      {/* Boutons */}
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => {
                            if (!acceptCGV && showAcceptModal?.id_devis !== d.id_devis) {
                              alert('Veuillez cocher la case d\'acceptation des CGV avant de continuer.');
                              return;
                            }
                            openAcceptModal(d);
                          }}
                          style={{
                            ...S.btnPrimary, background: theme.success,
                            opacity: acceptCGV || showAcceptModal?.id_devis === d.id_devis ? 1 : 0.6,
                          }}>
                          ✅ Accepter le devis
                        </button>
                        <button
                          onClick={() => refuserDevis(d.id_devis)}
                          style={{ ...S.btnPrimary, background: theme.danger }}>
                          ❌ Refuser
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Notes internes + Texte PDF — éditables admin / lecture client */}
                  <div style={{ marginTop: '14px' }}>
                    {editingNotesId === d.id_devis && isAdmin ? (
                      <div style={{ background: '#fffde7', border: '1px solid #f39c12',
                        borderRadius: '8px', padding: '12px' }}>
                        <div style={{ fontSize: '12px', fontWeight: '700', color: '#92400e',
                          marginBottom: '8px' }}>✏️ Modifier les notes</div>
                        <label style={{ fontSize: '11px', fontWeight: '600', color: '#92400e',
                          textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>
                          Remarques / Texte imprimé sur le devis
                        </label>
                        <textarea
                          value={editingNotesTmp.notes_pdf}
                          onChange={e => setEditingNotesTmp(p => ({ ...p, notes_pdf: e.target.value }))}
                          rows={3}
                          placeholder="Texte visible sur le PDF du devis (conditions, remarques client...)"
                          style={{ width: '100%', padding: '8px', border: '1px solid #fcd34d',
                            borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box',
                            resize: 'vertical', marginBottom: '8px', fontFamily: 'inherit' }}
                        />
                        <label style={{ fontSize: '11px', fontWeight: '600', color: '#92400e',
                          textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>
                          Notes internes (non imprimées)
                        </label>
                        <input type="text"
                          value={editingNotesTmp.notes}
                          onChange={e => setEditingNotesTmp(p => ({ ...p, notes: e.target.value }))}
                          placeholder="Notes internes uniquement..."
                          style={{ width: '100%', padding: '8px', border: '1px solid #fcd34d',
                            borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box',
                            marginBottom: '10px', fontFamily: 'inherit' }}
                        />
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => saveNotes(d.id_devis)}
                            disabled={savingNotes}
                            style={{ padding: '7px 16px', borderRadius: '6px', border: 'none',
                              cursor: 'pointer', fontWeight: '700', fontSize: '13px',
                              background: '#d97706', color: 'white',
                              opacity: savingNotes ? 0.6 : 1 }}>
                            {savingNotes ? '⏳ Sauvegarde...' : '💾 Enregistrer'}
                          </button>
                          <button
                            onClick={() => setEditingNotesId(null)}
                            style={{ padding: '7px 14px', borderRadius: '6px', border: 'none',
                              cursor: 'pointer', background: '#f3f4f6', color: '#666', fontSize: '13px' }}>
                            Annuler
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <div style={{ flex: 1 }}>
                          {d.notes_pdf && (
                            <div style={{ padding: '10px 14px', background: '#fffde7',
                              borderRadius: '6px', fontSize: '13px', color: '#555',
                              borderLeft: '3px solid #f39c12', marginBottom: '6px',
                              whiteSpace: 'pre-wrap' }}>
                              📋 <strong>Remarques :</strong> {d.notes_pdf}
                            </div>
                          )}
                          {isAdmin && d.notes && (
                            <div style={{ padding: '8px 12px', background: '#f9fafb',
                              borderRadius: '6px', fontSize: '12px', color: '#777',
                              borderLeft: '3px solid #d1d5db' }}>
                              📝 <em>{d.notes}</em>
                            </div>
                          )}
                          {!d.notes_pdf && isAdmin && !d.notes && (
                            <span style={{ fontSize: '12px', color: '#aaa', fontStyle: 'italic' }}>
                              Aucune remarque — cliquez ✏️ pour ajouter
                            </span>
                          )}
                        </div>
                        {isAdmin && (
                        <button
                          onClick={() => {
                            setEditingNotesTmp({ notes: d.notes || '', notes_pdf: d.notes_pdf || '' });
                            setEditingNotesId(d.id_devis);
                          }}
                          title="Modifier les notes"
                          style={{ padding: '5px 10px', borderRadius: '6px', border: 'none',
                            cursor: 'pointer', background: '#fef3c7', color: '#92400e',
                            fontSize: '13px', flexShrink: 0, whiteSpace: 'nowrap' }}>
                          ✏️ Modifier
                        </button>
                        )}
                      </div>
                    )}

                  {/* ── Tranches Optionnelles — admin complet / client lecture+activation ── */}
                  {(isAdmin || d.has_tranches) && (
                  <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '14px', marginTop: '14px' }}>
                    <TranchesPanel
                      devis={d}
                      config={config}
                      isMobile={isMobile}
                      onRefresh={fetchDevis}
                      isAdmin={isAdmin}
                    />
                  </div>
                  )}

                  {/* ── Annexes PDF — admin uniquement ───────────────────────── */}
                  {isAdmin && (
                  <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '14px', marginTop: '14px' }}>
                    <AnnexesPanel
                      devisId={d.id_devis}
                      config={config}
                      isMobile={isMobile}
                    />
                  </div>
                  )}
                  </div>
                </div>
              )}
            </div>
          );
        }); /* end filtered.map */
        })()}
      </div>
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', fontSize: '18px', color: theme.textSecondary },
  vide:    { textAlign: 'center', padding: '60px', color: theme.textSecondary },
};
const S = {
  overlay:     { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, overflowY: 'auto', padding: '16px 0' },
  modal:       { background: 'white', padding: '20px', borderRadius: '14px', width: '90%', maxWidth: '460px', boxShadow: theme.shadowLg, maxHeight: '90vh', overflowY: 'auto' },
  label:       { display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '13px', color: theme.textPrimary },
  input:       { width: '100%', padding: '9px 11px', border: `1px solid ${theme.border}`, borderRadius: '7px', fontSize: '13px', boxSizing: 'border-box', color: theme.textPrimary, marginBottom: '2px' },
  inlineInput: { padding: '4px 6px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px' },
  section:     { background: '#f8f9fa', borderRadius: '10px', padding: '16px', marginBottom: '16px' },
  sectionTitle:{ margin: '0 0 14px', fontSize: '14px', fontWeight: '700', color: theme.textPrimary },
  btnPrimary:  { padding: '9px 14px', background: theme.primary, color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' },
  btnSuccess:  { padding: '9px 14px', background: theme.success, color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' },
  btnGrey:     { padding: '9px 14px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' },
  btnSm:       { padding: '4px 8px', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' },
};
const TH = { padding: '10px 12px', textAlign: 'center', fontSize: '13px', fontWeight: '600' };
const TD = { padding: '10px 12px', fontSize: '13px' };

export default Devis;


// ═════════════════════════════════════════════════════════════════════════════
// TranchesPanel v3 — Postes par tranche, éditeur inline complet
// Même UX que l'éditeur de devis: description, unité, qty, prix, TVA
// ═════════════════════════════════════════════════════════════════════════════
function TranchesPanel({ devis, config, isMobile, onRefresh, isAdmin = true }) {
  const [tranches,     setTranches]     = React.useState([]);
  const [loading,      setLoading]      = React.useState(true);
  const [showAddForm,  setShowAddForm]  = React.useState(false);
  const [addForm,      setAddForm]      = React.useState({ code:'', libelle:'', validite_mois:6 });
  const [activModal,   setActivModal]   = React.useState(null);
  const [saving,       setSaving]       = React.useState(false);
  // Postes par tranche: { TA: [{...ligne},...], TB: [...] }
  const [postesByT,    setPostesByT]    = React.useState({});
  // Ligne en cours d'édition: { code_tranche, id_ligne }
  const [editingPoste, setEditingPoste] = React.useState(null);
  // Formulaire nouveau poste par tranche: { TA: {desc, unite, qty, pu, tva} }
  const [newPoste,     setNewPoste]     = React.useState({});
  const [addingPoste,  setAddingPoste]  = React.useState(null); // code_tranche en cours d'ajout

  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const UNITES = ['','h','FF','stuk','m²','ml','m','kg','kW','kWh','jr','pcs'];
  const TVA_OPTIONS = [0, 6, 12, 21];

  // ─── Chargement ──────────────────────────────────────────────────────────
  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      // Charger tranches ET lignes du devis en parallèle (source fraîche)
      const [rTranches, rDevis] = await Promise.all([
        axios.get(`${BASE}/devis/${devis.id_devis}/tranches`, config),
        axios.get(`${BASE}/devis/${devis.id_devis}`, config),
      ]);
      const tlist  = rTranches.data?.data || [];
      // Lignes fraîches depuis le backend (pas depuis le prop stale)
      const lignes = rDevis.data?.data?.LigneDevis
        || rDevis.data?.LigneDevis
        || devis.LigneDevis || [];
      setTranches(tlist);
      const byT = {};
      tlist.forEach(t => {
        byT[t.code] = lignes
          .filter(l => l.code_tranche === t.code)
          .sort((a,b) => (a.ordre||0) - (b.ordre||0));
      });
      setPostesByT(byT);
    } catch(e) { console.warn('tranches load', e.message); }
    setLoading(false);
  }, [devis.id_devis, config, BASE]);

  React.useEffect(() => { load(); }, [load]);

  // ─── Codes disponibles ───────────────────────────────────────────────────
  const CODES_USED = tranches.map(t => t.code);
  const nextCode = () => {
    for (const l of ['A','B','C','D','E','F','G','H']) {
      if (!CODES_USED.includes('T'+l)) return 'T'+l;
    }
    return 'T'+(CODES_USED.length+1);
  };

  // ─── Créer une tranche ───────────────────────────────────────────────────
  const saveTranche = async () => {
    if (!addForm.libelle.trim()) return alert('Donnez un libellé à cette tranche.');
    setSaving(true);
    try {
      await axios.post(`${BASE}/devis/${devis.id_devis}/tranches`,
        { code: addForm.code || nextCode(), libelle: addForm.libelle, validite_mois: addForm.validite_mois },
        config);
      setShowAddForm(false);
      setAddForm({ code:'', libelle:'', validite_mois:6 });
      await load();
      if (onRefresh) onRefresh();
    } catch(e) { alert('Erreur: '+(e.response?.data?.message||e.message)); }
    setSaving(false);
  };

  // ─── Ajouter un poste à une tranche ─────────────────────────────────────
  const addPoste = async (codeT) => {
    const np = newPoste[codeT] || {};
    if (!np.desc?.trim()) return alert('Description requise.');
    setSaving(true);
    try {
      const sorted = (devis.LigneDevis||[]).sort((a,b)=>(a.ordre||0)-(b.ordre||0));
      const maxOrdre = sorted.length ? Math.max(...sorted.map(l=>l.ordre||0)) : 0;
      await axios.post(`${BASE}/devis/${devis.id_devis}/ligne-libre`, {
        description_personnalisee: np.desc.trim(),
        unite:         np.unite || null,
        quantite:      parseFloat(np.qty) || 1,
        prix_unitaire: parseFloat(np.pu)  || 0,
        tva:           parseFloat(np.tva !== undefined ? np.tva : devis.taux_tva) || 0,
        type_ligne:    'article',
        ordre:         maxOrdre + 1,
        code_tranche:  codeT,
      }, config);
      setNewPoste(p => ({ ...p, [codeT]: {} }));
      setAddingPoste(null);
      await load();
      if (onRefresh) onRefresh();
    } catch(e) { alert('Erreur ajout poste: '+(e.response?.data?.message||e.message)); }
    setSaving(false);
  };

  // ─── Sauvegarder édition d'un poste ─────────────────────────────────────
  const savePoste = async (ligne) => {
    try {
      await axios.patch(`${BASE}/devis/ligne/${ligne.id_ligne_devis||ligne.id_ligne}`, {
        description_personnalisee: ligne.description_personnalisee,
        unite:         ligne.unite || null,
        quantite:      parseFloat(ligne.quantite) || 0,
        prix_unitaire: parseFloat(ligne.prix_unitaire) || 0,
        tva:           parseFloat(ligne.tva) || 0,
        code_tranche:  ligne.code_tranche,
      }, config);
      setEditingPoste(null);
      await load();
      if (onRefresh) onRefresh();
    } catch(e) { alert('Erreur save: '+(e.response?.data?.message||e.message)); }
  };

  // ─── Supprimer un poste ──────────────────────────────────────────────────
  const deletePoste = async (ligne) => {
    if (!window.confirm('Supprimer ce poste ?')) return;
    try {
      await axios.delete(`${BASE}/devis/${devis.id_devis}/ligne/${ligne.id_ligne_devis||ligne.id_ligne}`, config);
      await load();
      if (onRefresh) onRefresh();
    } catch(e) { alert('Erreur suppression.'); }
  };

  // ─── Modifier un poste inline ─────────────────────────────────────────────
  const updatePosteField = (codeT, idLigne, field, value) => {
    setPostesByT(prev => ({
      ...prev,
      [codeT]: (prev[codeT]||[]).map(l => {
        const lid = l.id_ligne_devis||l.id_ligne;
        return lid === idLigne ? { ...l, [field]: value } : l;
      })
    }));
  };

  // ─── Activer une tranche ─────────────────────────────────────────────────
  const today = () => new Date().toISOString().split('T')[0];
  const isMultiAnnee = (d) => d && devis.date_creation &&
    new Date(d).getFullYear() !== new Date(devis.date_creation).getFullYear();

  const activerTranche = async () => {
    if (!activModal) return;
    const { tranche, date, ref } = activModal;
    setSaving(true);
    try {
      const r = await axios.post(
        `${BASE}/devis/${devis.id_devis}/tranches/${tranche.code}/activer`,
        { date_activation: date, ref_bc_client: ref||null }, config);
      alert('Tranche '+tranche.code+' activée. OE: '+r.data?.data?.numero_oe);
      setActivModal(null);
      await load();
      if (onRefresh) onRefresh();
    } catch(e) { alert('Erreur: '+(e.response?.data?.message||e.message)); }
    setSaving(false);
  };

  const renouveler = async (t) => {
    const vm = prompt('Nouveau délai (mois):', '6');
    if (!vm) return;
    await axios.patch(`${BASE}/devis/${devis.id_devis}/tranches/${t.code}/renouveler`,
      { validite_mois: parseInt(vm) }, config);
    load();
  };

  const desactiver = async (t) => {
    if (!window.confirm('Désactiver la tranche "'+( t.libelle||t.code)+'" ?')) return;
    await axios.patch(`${BASE}/devis/${devis.id_devis}/tranches/${t.code}/annuler`, {}, config);
    load();
  };

  const supprimer = async (t) => {
    const postes = postesByT[t.code] || [];
    const msg = postes.length
      ? 'Supprimer la tranche "'+( t.libelle||t.code)+'" et ses '+postes.length+' poste(s) ?'
      : 'Supprimer la tranche "'+( t.libelle||t.code)+'" ?';
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${BASE}/devis/${devis.id_devis}/tranches/${t.code}`, config);
      await load();
      if (onRefresh) onRefresh();
    } catch(e) { alert('Erreur: '+(e.response?.data?.message||e.message)); }
  };

  // ─── Calculs ─────────────────────────────────────────────────────────────
  const calcTotaux = (postes) => {
    const ht = postes.reduce((s,l)=>s+(parseFloat(l.prix_unitaire)||0)*(parseFloat(l.quantite)||0),0);
    const tva = postes.reduce((s,l)=>s+(parseFloat(l.prix_unitaire)||0)*(parseFloat(l.quantite)||0)*(parseFloat(l.tva)||0)/100,0);
    return { ht, tva, ttc: ht+tva };
  };
  const fmt  = v => new Intl.NumberFormat('fr-BE',{style:'currency',currency:'EUR'}).format(v||0);
  const canActivate = (t) => devis.statut==='accepte' && t.statut==='en_attente';
  const verrouille  = ['facture','facture_partielle'].includes(devis.statut);
  const canEdit     = !verrouille;

  const STATUT_COLORS = { en_attente:'#f59e0b', activee:'#16a34a', expiree:'#dc2626', annulee:'#6b7280' };
  const STATUT_LABELS = { en_attente:'En attente', activee:'Activée ✅', expiree:'Expirée ⏰', annulee:'Désactivée' };

  const S = {
    btn: (bg,color) => ({ padding: isMobile?'7px 12px':'5px 10px', borderRadius:'6px',
      border:'none', cursor:'pointer', background:bg, color, fontSize:'12px', fontWeight:'600' }),
    th: { padding:'5px 8px', fontWeight:'700', fontSize:'11px', color:'#4f6d89',
      background:'#f0f4f8', borderBottom:'1px solid #e5e7eb', textAlign:'left' },
    td: { padding:'5px 8px', fontSize:'12px', borderBottom:'1px solid #f3f4f6', verticalAlign:'middle' },
    inp: { padding:'4px 6px', border:'1px solid #cbd5e1', borderRadius:'4px',
      fontSize:'12px', width:'100%', boxSizing:'border-box' },
  };

  return (
    <div style={{ marginTop:'18px' }}>
      {/* En-tête */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
        flexWrap:'wrap', gap:'8px', marginBottom:'12px' }}>
        <div>
          <span style={{ fontWeight:'800', fontSize:'13px', color:'#1a3a5c' }}>
            ✦ Tranches Optionnelles
          </span>
          {tranches.filter(t=>t.statut!=='annulee').length > 0 && (
            <span style={{ marginLeft:'8px', background:'#1a3a5c', color:'white',
              borderRadius:'12px', padding:'2px 9px', fontSize:'11px', fontWeight:'700' }}>
              {tranches.filter(t=>t.statut!=='annulee').length}
            </span>
          )}
          <div style={{ fontSize:'10px', color:'#9ca3af', marginTop:'2px' }}>
            Activables par bon de commande · Montants hors devis principal
          </div>
        </div>
        {isAdmin && canEdit && !showAddForm && (
          <button onClick={() => { setAddForm({ code:nextCode(), libelle:'', validite_mois:6 }); setShowAddForm(true); }}
            style={S.btn('#1a3a5c','white')}>
            + Ajouter une tranche optionnelle
          </button>
        )}
      </div>

      {/* Formulaire création tranche */}
      {showAddForm && (
        <div style={{ padding:'14px', background:'#eff6ff', borderRadius:'10px',
          border:'1px solid #bfdbfe', marginBottom:'14px' }}>
          <div style={{ fontWeight:'700', fontSize:'13px', color:'#1e40af', marginBottom:'10px' }}>
            Nouvelle tranche optionnelle
          </div>
          <div style={{ display:'grid', gridTemplateColumns: isMobile?'1fr':'80px 1fr 130px', gap:'8px', marginBottom:'10px' }}>
            <div>
              <label style={{ fontSize:'11px', color:'#6b7280', display:'block', marginBottom:'3px' }}>Code</label>
              <input value={addForm.code} maxLength={5}
                onChange={e => setAddForm(p=>({...p, code:e.target.value.toUpperCase()}))}
                placeholder="TA" style={{ ...S.inp, textAlign:'center', fontWeight:'800' }} />
            </div>
            <div>
              <label style={{ fontSize:'11px', color:'#6b7280', display:'block', marginBottom:'3px' }}>
                Libellé de la tranche *
              </label>
              <input value={addForm.libelle} autoFocus
                onChange={e => setAddForm(p=>({...p, libelle:e.target.value}))}
                placeholder="ex: Option toiture zinc, Isolation combles..."
                style={S.inp} />
            </div>
            <div>
              <label style={{ fontSize:'11px', color:'#6b7280', display:'block', marginBottom:'3px' }}>Validité</label>
              <select value={addForm.validite_mois}
                onChange={e => setAddForm(p=>({...p, validite_mois:parseInt(e.target.value)}))}
                style={{ ...S.inp }}>
                {[3,6,9,12,18,24].map(m => <option key={m} value={m}>{m} mois</option>)}
              </select>
            </div>
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={saveTranche} disabled={saving} style={S.btn('#1d4ed8','white')}>
              {saving ? '⏳...' : '✓ Créer la tranche'}
            </button>
            <button onClick={() => setShowAddForm(false)} style={S.btn('#f3f4f6','#6b7280')}>Annuler</button>
          </div>
          <div style={{ marginTop:'8px', fontSize:'10px', color:'#3b82f6' }}>
            ⓘ Après création, ajoutez les postes directement dans la carte de la tranche
          </div>
        </div>
      )}

      {/* Liste tranches */}
      {loading ? (
        <p style={{ color:'#9ca3af', fontSize:'12px' }}>Chargement...</p>
      ) : tranches.filter(t=>t.statut!=='annulee').length === 0 ? (
        <div style={{ padding:'16px', background:'#f8fafc', borderRadius:'8px',
          border:'1px dashed #cbd5e1', textAlign:'center' }}>
          <p style={{ margin:0, fontSize:'12px', color:'#9ca3af' }}>
            Aucune tranche optionnelle — devis en mode standard
          </p>
        </div>
      ) : (
        tranches.filter(t=>t.statut!=='annulee').map(t => {
          const postes  = postesByT[t.code] || [];
          const totaux  = calcTotaux(postes);
          const editing = editingPoste?.codeT === t.code ? editingPoste.idLigne : null;
          const adding  = addingPoste === t.code;
          const np      = newPoste[t.code] || {};

          return (
            <div key={t.id_tranche} style={{ marginBottom:'12px', borderRadius:'10px',
              border:'1px solid '+(t.statut==='activee'?'#86efac':t.statut==='expiree'?'#fca5a5':'#e5e7eb'),
              borderLeft:'4px solid '+(STATUT_COLORS[t.statut]||'#e5e7eb'),
              background:'white', overflow:'hidden' }}>

              {/* ── En-tête tranche ── */}
              <div style={{ padding:'12px 14px', display:'flex', justifyContent:'space-between',
                alignItems:'flex-start', flexWrap:'wrap', gap:'8px',
                background: t.statut==='activee'?'#f0fdf4':t.statut==='expiree'?'#fef2f2':'#f8fafc' }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
                    <span style={{ fontWeight:'800', fontSize:'14px', color:'#1a3a5c' }}>
                      Tranche {t.code.replace('T','')}
                    </span>
                    <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:'10px',
                      fontSize:'10px', fontWeight:'700',
                      background:(STATUT_COLORS[t.statut]||'#9ca3af')+'22',
                      color:(STATUT_COLORS[t.statut]||'#9ca3af') }}>
                      {STATUT_LABELS[t.statut]||t.statut}
                    </span>
                  </div>
                  <div style={{ fontSize:'13px', color:'#4f6d89', marginTop:'2px', fontWeight:'600' }}>
                    {t.libelle||'Sans libellé'}
                  </div>
                  <div style={{ fontSize:'10px', color:'#9ca3af', marginTop:'2px' }}>
                    {t.date_expiration
                      ? `Valide jusqu\u2019au ${new Date(t.date_expiration).toLocaleDateString('fr-BE')}`
                      : `Validit\u00e9 \u00e0 d\u00e9finir \u00e0 l\u2019acceptation du devis`}
                    {t.ref_bc_client && (' \u00b7 BC: '+t.ref_bc_client)}
                  </div>
                </div>
                <div style={{ textAlign:'right', flexShrink:0 }}>
                  <div style={{ fontSize:'16px', fontWeight:'800', color:'#1a3a5c' }}>
                    {fmt(totaux.ttc)}
                  </div>
                  <div style={{ fontSize:'10px', color:'#9ca3af' }}>
                    HT: {fmt(totaux.ht)}
                    {totaux.tva > 0 && (' \u00b7 TVA: '+fmt(totaux.tva))}
                  </div>
                </div>
              </div>

              {/* ── Tableau des postes ── */}
              <div style={{ padding:'0 14px 10px' }}>
                {postes.length > 0 && (
                  <div style={{ overflowX:'auto', marginTop:'8px' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                      <thead>
                        <tr>
                          <th style={{ ...S.th, width:'35%' }}>Description</th>
                          <th style={{ ...S.th, width:'8%' }}>Unité</th>
                          <th style={{ ...S.th, width:'8%', textAlign:'right' }}>Qté</th>
                          <th style={{ ...S.th, width:'12%', textAlign:'right' }}>Prix unit.</th>
                          <th style={{ ...S.th, width:'8%', textAlign:'center' }}>TVA%</th>
                          <th style={{ ...S.th, width:'12%', textAlign:'right' }}>Total HT</th>
                          {isAdmin && canEdit && <th style={{ ...S.th, width:'8%' }}></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {postes.map(ligne => {
                          const lid = ligne.id_ligne_devis||ligne.id_ligne;
                          const isEdit = editing === lid;
                          const ht = (parseFloat(ligne.prix_unitaire)||0)*(parseFloat(ligne.quantite)||0);
                          return (
                            <tr key={lid} style={{ background: isEdit?'#fffbeb':'white' }}>
                              <td style={S.td}>
                                {isEdit
                                  ? <input value={ligne.description_personnalisee||''} style={S.inp}
                                      onChange={e=>updatePosteField(t.code,lid,'description_personnalisee',e.target.value)} />
                                  : <span style={{ fontWeight:'500' }}>{ligne.description_personnalisee}</span>}
                              </td>
                              <td style={S.td}>
                                {isEdit
                                  ? <select value={ligne.unite||''} style={{ ...S.inp, width:'60px' }}
                                      onChange={e=>updatePosteField(t.code,lid,'unite',e.target.value)}>
                                      {UNITES.map(u=><option key={u} value={u}>{u||'—'}</option>)}
                                    </select>
                                  : (ligne.unite||'—')}
                              </td>
                              <td style={{ ...S.td, textAlign:'right' }}>
                                {isEdit
                                  ? <input type="number" value={ligne.quantite} style={{ ...S.inp, width:'55px', textAlign:'right' }}
                                      onChange={e=>updatePosteField(t.code,lid,'quantite',e.target.value)} />
                                  : parseFloat(ligne.quantite||0)}
                              </td>
                              <td style={{ ...S.td, textAlign:'right' }}>
                                {isEdit
                                  ? <input type="number" value={ligne.prix_unitaire} style={{ ...S.inp, width:'70px', textAlign:'right' }}
                                      onChange={e=>updatePosteField(t.code,lid,'prix_unitaire',e.target.value)} />
                                  : fmt(parseFloat(ligne.prix_unitaire||0))}
                              </td>
                              <td style={{ ...S.td, textAlign:'center' }}>
                                {isEdit
                                  ? <select value={parseFloat(ligne.tva)||0} style={{ ...S.inp, width:'55px', textAlign:'center' }}
                                      onChange={e=>updatePosteField(t.code,lid,'tva',e.target.value)}>
                                      {TVA_OPTIONS.map(v=><option key={v} value={v}>{v}%</option>)}
                                    </select>
                                  : (parseFloat(ligne.tva||0)+'%')}
                              </td>
                              <td style={{ ...S.td, textAlign:'right', fontWeight:'700', color:'#1a3a5c' }}>
                                {fmt(ht)}
                              </td>
                              {isAdmin && canEdit && (
                                <td style={{ ...S.td, textAlign:'center' }}>
                                  <div style={{ display:'flex', gap:'3px', justifyContent:'center' }}>
                                    {isEdit ? (
                                      <>
                                        <button onClick={()=>savePoste(ligne)}
                                          style={{ ...S.btn('#16a34a','white'), padding:'3px 8px', fontSize:'11px' }}>✓</button>
                                        <button onClick={()=>setEditingPoste(null)}
                                          style={{ ...S.btn('#f3f4f6','#6b7280'), padding:'3px 8px', fontSize:'11px' }}>✕</button>
                                      </>
                                    ) : (
                                      <>
                                        <button onClick={()=>setEditingPoste({ codeT:t.code, idLigne:lid })}
                                          style={{ ...S.btn('#f0f9ff','#0369a1'), padding:'3px 7px', fontSize:'11px' }}>✏️</button>
                                        <button onClick={()=>deletePoste(ligne)}
                                          style={{ ...S.btn('#fef2f2','#dc2626'), padding:'3px 7px', fontSize:'11px' }}>🗑</button>
                                      </>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* ── Formulaire ajout poste ── */}
                {isAdmin && canEdit && adding && (
                  <div style={{ marginTop:'8px', padding:'10px', background:'#f0f4ff',
                    borderRadius:'8px', border:'1px solid #c7d2fe' }}>
                    <div style={{ display:'grid',
                      gridTemplateColumns: isMobile?'1fr 1fr':'2fr 80px 70px 90px 70px',
                      gap:'6px', marginBottom:'6px' }}>
                      <div>
                        <label style={{ fontSize:'10px', color:'#6b7280', display:'block', marginBottom:'2px' }}>
                          Description *
                        </label>
                        <input value={np.desc||''} autoFocus
                          onChange={e=>setNewPoste(p=>({...p,[t.code]:{...p[t.code],desc:e.target.value}}))}
                          placeholder="Descriptif du poste..."
                          style={S.inp} />
                      </div>
                      <div>
                        <label style={{ fontSize:'10px', color:'#6b7280', display:'block', marginBottom:'2px' }}>
                          Unité
                        </label>
                        <select value={np.unite||''}
                          onChange={e=>setNewPoste(p=>({...p,[t.code]:{...p[t.code],unite:e.target.value}}))}
                          style={S.inp}>
                          {UNITES.map(u=><option key={u} value={u}>{u||'—'}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize:'10px', color:'#6b7280', display:'block', marginBottom:'2px' }}>
                          Quantité
                        </label>
                        <input type="number" value={np.qty||1}
                          onChange={e=>setNewPoste(p=>({...p,[t.code]:{...p[t.code],qty:e.target.value}}))}
                          style={{ ...S.inp, textAlign:'right' }} />
                      </div>
                      <div>
                        <label style={{ fontSize:'10px', color:'#6b7280', display:'block', marginBottom:'2px' }}>
                          Prix unit. HT
                        </label>
                        <input type="number" value={np.pu||0}
                          onChange={e=>setNewPoste(p=>({...p,[t.code]:{...p[t.code],pu:e.target.value}}))}
                          style={{ ...S.inp, textAlign:'right' }} />
                      </div>
                      <div>
                        <label style={{ fontSize:'10px', color:'#6b7280', display:'block', marginBottom:'2px' }}>
                          TVA %
                        </label>
                        <select value={np.tva !== undefined ? np.tva : (devis.taux_tva||6)}
                          onChange={e=>setNewPoste(p=>({...p,[t.code]:{...p[t.code],tva:parseFloat(e.target.value)}}))}
                          style={S.inp}>
                          {TVA_OPTIONS.map(v=><option key={v} value={v}>{v}%</option>)}
                        </select>
                      </div>
                    </div>
                    {/* Total calculé */}
                    {np.qty && np.pu && (
                      <div style={{ fontSize:'11px', color:'#4f6d89', marginBottom:'6px' }}>
                        Total HT: <strong>{fmt((parseFloat(np.qty)||0)*(parseFloat(np.pu)||0))}</strong>
                        {' · '}
                        TTC: <strong>{fmt((parseFloat(np.qty)||0)*(parseFloat(np.pu)||0)*(1+(parseFloat(np.tva||devis.taux_tva||6)/100)))}</strong>
                      </div>
                    )}
                    <div style={{ display:'flex', gap:'6px' }}>
                      <button onClick={()=>addPoste(t.code)} disabled={saving}
                        style={S.btn('#1d4ed8','white')}>
                        {saving ? '⏳...' : '✓ Ajouter ce poste'}
                      </button>
                      <button onClick={()=>setAddingPoste(null)} style={S.btn('#f3f4f6','#6b7280')}>
                        Annuler
                      </button>
                    </div>
                  </div>
                )}

                {/* ── Bouton + Ajouter poste ── */}
                {isAdmin && canEdit && !adding && (
                  <button onClick={()=>setAddingPoste(t.code)}
                    style={{ marginTop:'8px', padding:'5px 12px', borderRadius:'6px',
                      border:'1px dashed #93c5fd', background:'transparent',
                      cursor:'pointer', color:'#3b82f6', fontSize:'11px', fontWeight:'600' }}>
                    + Ajouter un poste à cette tranche
                  </button>
                )}
              </div>

              {/* ── Actions tranche ── */}
              <div style={{ padding:'8px 14px', borderTop:'1px solid #f3f4f6',
                display:'flex', gap:'6px', flexWrap:'wrap',
                background: t.statut==='activee'?'#f0fdf4':'white' }}>
                {canActivate(t) && isAdmin && (
                  <button onClick={()=>setActivModal({tranche:t,date:today(),ref:''})}
                    style={S.btn('#16a34a','white')}>
                    ▶ Activer cette tranche
                  </button>
                )}
                {canActivate(t) && !isAdmin && (
                  <button onClick={async ()=>{
                    if (!window.confirm(`Confirmer l'activation de la tranche "${t.libelle||t.code}" pour ${fmt(calcTotaux(postesByT[t.code]||[]).ttc)} TTC ?`)) return;
                    try {
                      await axios.post(`${BASE}/devis/${devis.id_devis}/tranches/${t.code}/activer-client`, {}, config);
                      alert('✅ Tranche activée — un email de confirmation vous sera envoyé.');
                      await load();
                      if (onRefresh) onRefresh();
                    } catch(e) { alert('Erreur: '+(e.response?.data?.message||e.message)); }
                  }} style={S.btn('#16a34a','white')}>
                    ▶ Activer cette tranche
                  </button>
                )}
                {isAdmin && t.statut==='expiree' && (
                  <button onClick={()=>renouveler(t)} style={S.btn('#f59e0b','white')}>
                    ↺ Renouveler
                  </button>
                )}
                {t.statut==='activee' && t.id_devis_oe && (
                  <div style={{ display:'flex', gap:'6px', alignItems:'center', flexWrap:'wrap' }}>
                    <span style={{ fontSize:'11px', color:'#16a34a' }}>
                      OE: {t.numero_oe||('DEV#'+t.id_devis_oe)}
                    </span>
                    <button
                      onClick={async () => {
                        try {
                          const r = await axios.get(
                            `${BASE}/devis/${devis.id_devis}/tranches/${t.code}/oe-pdf`,
                            { ...config, responseType: 'blob' }
                          );
                          const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                          const a = document.createElement('a');
                          a.href = url;
                          a.setAttribute('download', `OE-${t.numero_oe || t.code}.pdf`);
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                        } catch(e) { alert('Erreur OE PDF: ' + (e.response?.data?.message || e.message)); }
                      }}
                      title="Télécharger le PDF Ordre d'Exécution"
                      style={{ padding:'4px 10px', borderRadius:'6px', background:'#f0fdf4',
                        color:'#16a34a', fontSize:'11px', fontWeight:'700',
                        border:'none', cursor:'pointer' }}>
                      🖨 OE PDF
                    </button>
                  </div>
                )}
                {isAdmin && t.statut==='en_attente' && canEdit && (
                  <button onClick={()=>desactiver(t)} title="Désactiver — conservée pour traçabilité"
                    style={S.btn('#fef2f2','#dc2626')}>
                    ✕ Désactiver
                  </button>
                )}
                {isAdmin && t.statut==='en_attente' && canEdit && (
                  <button onClick={()=>supprimer(t)} style={S.btn('#f3f4f6','#9ca3af')}>
                    🗑 Supprimer
                  </button>
                )}
              </div>
            </div>
          );
        })
      )}

      {/* ─ Info modes validation ─ */}
      {devis.statut === 'accepte' && tranches.filter(t=>t.statut==='en_attente').length > 0 && (
        <div style={{ marginTop:'10px', padding:'10px 12px', background:'#eff6ff',
          border:'1px solid #bfdbfe', borderRadius:'8px', fontSize:'11px', color:'#1e40af' }}>
          <div style={{ fontWeight:'700', marginBottom:'4px' }}>💡 Comment activer une tranche ?</div>
          <div style={{ lineHeight:1.6 }}>
            <div>📧 <strong>Via l'espace client</strong> — le client peut activer depuis son interface</div>
            <div>📱 <strong>Par email / WhatsApp / tél.</strong> — cliquez "▶ Activer" et notez la référence</div>
            <div>🖨 <strong>Remise en main propre</strong> — activez manuellement, imprimez le PDF OE via "🖨 OE PDF"</div>
          </div>
        </div>
      )}

      {/* Tranches désactivées */}
      {tranches.filter(t=>t.statut==='annulee').length > 0 && (
        <div style={{ fontSize:'11px', color:'#9ca3af', marginTop:'6px', textAlign:'center' }}>
          {tranches.filter(t=>t.statut==='annulee').length} tranche(s)
          {' '}désactivée(s) — conservées pour traçabilité
        </div>
      )}

      {/* ── Modal activation ── */}
      {activModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:'16px' }}>
          <div style={{ background:'white', borderRadius:'14px', padding:'24px',
            maxWidth:'480px', width:'100%', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ margin:'0 0 12px', color:'#1a3a5c', fontSize:'16px' }}>
              ▶ Activer Tranche {activModal.tranche.code.replace('T','')}
            </h3>
            <p style={{ fontSize:'13px', color:'#4f6d89', margin:'0 0 14px' }}>
              <strong>{activModal.tranche.libelle||activModal.tranche.code}</strong>
              {' — '}{fmt(calcTotaux(postesByT[activModal.tranche.code]||[]).ttc)} TTC
            </p>
            {isMultiAnnee(activModal.date) && (
              <div style={{ padding:'10px', background:'#fffbeb', border:'1px solid #fcd34d',
                borderRadius:'8px', marginBottom:'14px', fontSize:'12px', color:'#92400e' }}>
                ⚠️ <strong>Activation en {new Date(activModal.date).getFullYear()}</strong>
                {' — '}devis de {new Date(devis.date_creation).getFullYear()}.
                Vérifiez TVA et prix. Cette facture = CA {new Date(activModal.date).getFullYear()}.
              </div>
            )}
            <div style={{ marginBottom:'10px' }}>
              <label style={{ fontSize:'12px', fontWeight:'600', color:'#4f6d89', display:'block', marginBottom:'4px' }}>
                Date d'activation *
              </label>
              <input type="date" value={activModal.date}
                onChange={e=>setActivModal(p=>({...p,date:e.target.value}))}
                style={{ width:'100%', padding:'9px', border:'1px solid #d1d5db',
                  borderRadius:'6px', fontSize:'14px', boxSizing:'border-box' }} />
            </div>
            <div style={{ marginBottom:'16px' }}>
              <label style={{ fontSize:'12px', fontWeight:'600', color:'#4f6d89', display:'block', marginBottom:'4px' }}>
                Référence BC client (optionnel)
              </label>
              <input value={activModal.ref}
                onChange={e=>setActivModal(p=>({...p,ref:e.target.value}))}
                placeholder="Email du 14/02/2027, BC-042, Accord tel..."
                style={{ width:'100%', padding:'9px', border:'1px solid #d1d5db',
                  borderRadius:'6px', fontSize:'13px', boxSizing:'border-box' }} />
            </div>
            <div style={{ display:'flex', gap:'10px' }}>
              <button onClick={activerTranche} disabled={saving}
                style={{ flex:1, padding:'11px', borderRadius:'8px', border:'none',
                  cursor:saving?'not-allowed':'pointer',
                  background:saving?'#94a3b8':'#16a34a', color:'white',
                  fontWeight:'700', fontSize:'14px' }}>
                {saving ? "⏳ Activation..." : "✅ Confirmer l'activation"}
              </button>
              <button onClick={()=>setActivModal(null)}
                style={{ padding:'11px 18px', borderRadius:'8px', border:'none',
                  cursor:'pointer', background:'#f3f4f6', color:'#6b7280', fontSize:'13px' }}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════════
// AnnexesPanel v3 — Multi-upload, renommage immédiat, mobile-friendly
// Nombre illimité · Titres libres · PC + iPhone + iPad + Android
// ═════════════════════════════════════════════════════════════════════════════
function AnnexesPanel({ devisId, config, isMobile }) {
  const [annexes,     setAnnexes]     = React.useState([]);
  const [loading,     setLoading]     = React.useState(true);
  // Upload multiple : { idx, total, current } ou null
  const [uploadProgress, setUploadProgress] = React.useState(null);
  // Renommage : { id: id_annexe, val: string } ou null
  const [renaming,    setRenaming]    = React.useState(null);
  const [saving,      setSaving]      = React.useState(false);
  const fileRef = React.useRef();
  const renameRef = React.useRef();
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // ── Charger les annexes ─────────────────────────────────────────────────
  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BASE}/devis/${devisId}/annexes`, config);
      setAnnexes(r.data?.data || []);
    } catch(e) {}
    setLoading(false);
  }, [devisId, config, BASE]);

  React.useEffect(() => { load(); }, [load]);

  // Focus auto sur le champ de renommage
  React.useEffect(() => {
    if (renaming && renameRef.current) renameRef.current.focus();
  }, [renaming]);

  // ── Upload (un ou plusieurs fichiers) ──────────────────────────────────
  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    const pdfs = Array.from(files).filter(f => f.type === 'application/pdf');
    const notPdf = files.length - pdfs.length;
    if (notPdf > 0) alert(notPdf + ' fichier(s) ignoré(s) — seuls les PDF sont acceptés.');
    if (pdfs.length === 0) return;

    const oversized = pdfs.filter(f => f.size > 30 * 1024 * 1024);
    if (oversized.length > 0) {
      alert('Fichier(s) trop volumineux (max 30 MB) : ' + oversized.map(f => f.name).join(', '));
      return;
    }

    // Upload séquentiel avec compteur
    const newAnnexes = [];
    for (let i = 0; i < pdfs.length; i++) {
      setUploadProgress({ idx: i + 1, total: pdfs.length, name: pdfs[i].name });
      const fd = new FormData();
      fd.append('pdf', pdfs[i]);
      // Titre initial = nom du fichier sans extension
      fd.append('nom_affichage', pdfs[i].name.replace(/\.pdf$/i, '').trim() || 'Annexe');
      try {
        const r = await axios.post(`${BASE}/devis/${devisId}/annexes`, fd,
          { headers: { ...config.headers, 'Content-Type': 'multipart/form-data' } });
        if (r.data?.data) newAnnexes.push(r.data.data);
      } catch(e) {
        alert('Erreur pour "' + pdfs[i].name + '": ' + (e.response?.data?.message || e.message));
      }
    }
    setUploadProgress(null);
    if (fileRef.current) fileRef.current.value = '';

    // Mettre à jour la liste et passer le premier nouvel item en mode renommage
    await load();
    // Proposer de renommer le premier ajouté (le plus courant = 1 fichier)
    if (newAnnexes.length === 1) {
      setRenaming({ id: newAnnexes[0].id_annexe, val: newAnnexes[0].nom_affichage });
    }
    // Si plusieurs : tous passent en mode renommage via un state queue
    // (simplifié : l'admin voit les nouvelles lignes avec le nom auto et peut renommer chacune)
  };

  // ── Sauvegarder le renommage ──────────────────────────────────────────
  const saveRename = async () => {
    if (!renaming || !renaming.val.trim()) { setRenaming(null); return; }
    setSaving(true);
    try {
      await axios.patch(`${BASE}/devis/${devisId}/annexes/${renaming.id}/rename`,
        { nom_affichage: renaming.val.trim() }, config);
      setAnnexes(prev => prev.map(a =>
        a.id_annexe === renaming.id ? { ...a, nom_affichage: renaming.val.trim() } : a
      ));
    } catch(e) { alert('Erreur renommage.'); }
    setSaving(false);
    setRenaming(null);
  };

  // ── Supprimer ────────────────────────────────────────────────────────
  const del = async (annId, nom) => {
    if (!window.confirm(`Supprimer l'annexe "${nom}" ?`)) return;
    try {
      await axios.delete(`${BASE}/devis/${devisId}/annexes/${annId}`, config);
      setAnnexes(prev => prev.filter(a => a.id_annexe !== annId));
    } catch(e) { alert('Erreur suppression.'); }
  };

  // ── Réordonner : drag (desktop) ou boutons ▲▼ (mobile) ────────────────
  const moveAnnexe = async (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= annexes.length) return;
    const arr = [...annexes];
    const [m] = arr.splice(fromIdx, 1);
    arr.splice(toIdx, 0, m);
    setAnnexes(arr);
    try {
      await axios.patch(`${BASE}/devis/${devisId}/annexes/reorder`,
        { ordre: arr.map(a => a.id_annexe) }, config);
    } catch(e) {}
  };

  // Drag state (desktop)
  const [dragIdx, setDragIdx] = React.useState(null);
  const [dragOver, setDragOver] = React.useState(null);
  const onDragStart = (i) => setDragIdx(i);
  const onDragOver  = (e, i) => { e.preventDefault(); setDragOver(i); };
  const onDrop      = (i) => {
    if (dragIdx !== null && dragIdx !== i) moveAnnexe(dragIdx, i);
    setDragIdx(null); setDragOver(null);
  };

  const fmtSize = (b) => !b ? '' : b >= 1024*1024
    ? (b/1024/1024).toFixed(1) + ' MB'
    : Math.round(b/1024) + ' KB';

  const btnBase = {
    border: 'none', cursor: 'pointer', borderRadius: '6px',
    fontFamily: 'inherit', fontWeight: '600',
  };

  // ── Rendu ─────────────────────────────────────────────────────────────
  return (
    <div style={{ marginTop: '18px' }}>

      {/* ── En-tête ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
        <div>
          <span style={{ fontWeight: '800', fontSize: '13px', color: '#1a3a5c' }}>
            📎 Annexes PDF
          </span>
          {annexes.length > 0 && (
            <span style={{ marginLeft: '8px', background: '#1a3a5c', color: 'white',
              borderRadius: '12px', padding: '2px 9px', fontSize: '11px', fontWeight: '700' }}>
              {annexes.length}
            </span>
          )}
          <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>
            Nombre illimité · Fiches techniques, certifications, plans...
          </div>
        </div>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={!!uploadProgress}
          style={{ ...btnBase, padding: isMobile ? '11px 18px' : '8px 16px',
            background: uploadProgress ? '#94a3b8' : '#1a3a5c', color: 'white',
            fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px',
            opacity: uploadProgress ? 0.7 : 1 }}>
          {uploadProgress
            ? `⏳ ${uploadProgress.idx}/${uploadProgress.total}…`
            : (isMobile ? '⬆ Ajouter PDF(s)' : '⬆ Ajouter un ou plusieurs PDF')}
        </button>
        {/* Input multi-fichiers — iPhone ouvre l'app Fichiers, Android le gestionnaire */}
        <input
          ref={fileRef} type="file" accept="application/pdf" multiple
          style={{ display: 'none' }}
          onChange={e => uploadFiles(e.target.files)}
        />
      </div>

      {/* ── Zone principale ── */}
      <div
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); uploadFiles(e.dataTransfer.files); }}
        style={{ border: '2px dashed #cbd5e1', borderRadius: '12px',
          background: '#f8fafc',
          padding: loading || (annexes.length === 0 && !uploadProgress) ? '28px 16px' : '6px',
          minHeight: '88px', transition: 'background 0.2s' }}>

        {/* Progress upload */}
        {uploadProgress && (
          <div style={{ padding: '12px', background: '#eff6ff', borderRadius: '8px',
            marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#1e40af' }}>
                Upload en cours — {uploadProgress.idx} / {uploadProgress.total}
              </div>
              <div style={{ fontSize: '11px', color: '#3b82f6', marginTop: '2px' }}>
                {uploadProgress.name}
              </div>
              <div style={{ marginTop: '5px', height: '4px', background: '#bfdbfe', borderRadius: '2px' }}>
                <div style={{ height: '4px', background: '#2563eb', borderRadius: '2px',
                  width: Math.round(uploadProgress.idx / uploadProgress.total * 100) + '%',
                  transition: 'width 0.3s' }} />
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: '12px', margin: 0 }}>
            Chargement…
          </p>
        ) : annexes.length === 0 && !uploadProgress ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '8px', opacity: 0.4 }}>📁</div>
            <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>
              Aucune annexe pour ce devis
            </p>
            <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>
              {isMobile
                ? "Appuyez sur “⬆ Ajouter PDF(s)” — votre app Fichiers s’ouvrira"
                : 'Glissez-déposez vos PDF ici (sélection multiple possible)'}
            </p>
            <p style={{ margin: '6px 0 0', fontSize: '10px', color: '#cbd5e1' }}>
              Fiches techniques · Certifications · Plans · Notices · max 30 MB/fichier
            </p>
          </div>
        ) : (
          <div>
            {annexes.map((ann, i) => {
              const isRenaming = renaming && renaming.id === ann.id_annexe;
              const isDragTarget = dragOver === i;
              return (
                <div key={ann.id_annexe}
                  draggable={!isMobile && !isRenaming}
                  onDragStart={() => onDragStart(i)}
                  onDragOver={e => onDragOver(e, i)}
                  onDrop={() => onDrop(i)}
                  onDragEnd={() => { setDragIdx(null); setDragOver(null); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '8px',
                    padding: isMobile ? '10px 8px' : '9px 12px',
                    margin: '3px 0', borderRadius: '8px',
                    background: isDragTarget ? '#dbeafe' : isRenaming ? '#fffbeb' : 'white',
                    border: '1px solid ' + (isDragTarget ? '#3b82f6' : isRenaming ? '#fcd34d' : '#e5e7eb'),
                    transition: 'all 0.15s',
                    cursor: isMobile ? 'default' : (isRenaming ? 'text' : 'grab'),
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                  }}>
                  {/* Numéro badge */}
                  <div style={{ width: '24px', height: '24px', borderRadius: '50%',
                    background: '#1a3a5c', color: 'white', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '11px', fontWeight: '800' }}>
                    {i + 1}
                  </div>
                  {/* Icône */}
                  <span style={{ fontSize: isMobile ? '20px' : '17px', flexShrink: 0 }}>📄</span>
                  {/* Nom / champ renommage */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {isRenaming ? (
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <input
                          ref={renameRef}
                          value={renaming.val}
                          onChange={e => setRenaming(r => ({ ...r, val: e.target.value }))}
                          onKeyDown={e => { if(e.key==='Enter') saveRename(); if(e.key==='Escape') setRenaming(null); }}
                          placeholder="Ex: Fiche technique tuile Marseille 20×25"
                          style={{ flex: 1, minWidth: isMobile ? '100%' : '180px',
                            padding: '5px 10px', border: '2px solid #f59e0b',
                            borderRadius: '6px', fontSize: '13px', fontFamily: 'inherit',
                            background: 'white', boxSizing: 'border-box' }}
                        />
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button onClick={saveRename} disabled={saving}
                            style={{ ...btnBase, padding: '5px 12px',
                              background: '#16a34a', color: 'white', fontSize: '12px' }}>
                            {saving ? '…' : '✓ OK'}
                          </button>
                          <button onClick={() => setRenaming(null)}
                            style={{ ...btnBase, padding: '5px 10px',
                              background: '#f3f4f6', color: '#6b7280', fontSize: '12px' }}>
                            ✕
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '13px', color: '#1a1a2e',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ann.nom_affichage}
                        </div>
                        {ann.taille_octets > 0 && (
                          <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '1px' }}>
                            PDF · {fmtSize(ann.taille_octets)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Boutons ▲▼ (mobile) ou drag hint (desktop) */}
                  {!isRenaming && isMobile && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flexShrink: 0 }}>
                      <button onClick={() => moveAnnexe(i, i-1)} disabled={i===0}
                        style={{ ...btnBase, padding: '3px 8px', background: i===0 ? '#f3f4f6' : '#e0f2fe',
                          color: i===0 ? '#d1d5db' : '#0369a1', fontSize: '12px' }}>▲</button>
                      <button onClick={() => moveAnnexe(i, i+1)} disabled={i===annexes.length-1}
                        style={{ ...btnBase, padding: '3px 8px', background: i===annexes.length-1 ? '#f3f4f6' : '#e0f2fe',
                          color: i===annexes.length-1 ? '#d1d5db' : '#0369a1', fontSize: '12px' }}>▼</button>
                    </div>
                  )}
                  {!isRenaming && !isMobile && (
                    <span style={{ fontSize: '14px', color: '#cbd5e1', cursor: 'grab',
                      userSelect: 'none', flexShrink: 0, paddingRight: '4px' }} title="Glisser pour réordonner">
                      ⠿
                    </span>
                  )}
                  {/* Actions Télécharger / Renommer / Supprimer */}
                  {!isRenaming && (
                    <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                      <a href={`${BASE}/devis/${devisId}/annexes/${ann.id_annexe}/download`}
                        target="_blank" rel="noreferrer" title="Ouvrir le PDF"
                        style={{ ...btnBase, padding: isMobile ? '7px 10px' : '5px 9px',
                          background: '#f0fdf4', color: '#16a34a', fontSize: '13px',
                          textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
                        ⬇
                      </a>
                      <button title="Renommer"
                        onClick={() => setRenaming({ id: ann.id_annexe, val: ann.nom_affichage })}
                        style={{ ...btnBase, padding: isMobile ? '7px 10px' : '5px 9px',
                          background: '#fffbeb', color: '#92400e', fontSize: '13px' }}>
                        ✏️
                      </button>
                      <button title="Supprimer"
                        onClick={() => del(ann.id_annexe, ann.nom_affichage)}
                        style={{ ...btnBase, padding: isMobile ? '7px 10px' : '5px 9px',
                          background: '#fef2f2', color: '#dc2626', fontSize: '13px' }}>
                        🗑
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            {!isMobile && annexes.length > 1 && (
              <div style={{ textAlign: 'center', fontSize: '10px', color: '#9ca3af', marginTop: '5px' }}>
                ⠿ Glissez pour réordonner · L'ordre définit la numérotation dans le PDF du devis
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Info bar récap ── */}
      {annexes.length > 0 && (
        <div style={{ marginTop: '10px', padding: '10px 14px',
          background: '#f0fdf4', border: '1px solid #86efac',
          borderRadius: '8px', fontSize: '11px', color: '#15803d', lineHeight: 1.6 }}>
          <div style={{ fontWeight: '700', marginBottom: '3px' }}>
            ✅ {annexes.length} annexe{annexes.length>1?'s':''}  · Fichiers PDF séparés
          </div>
          <div>
            {annexes.map((a,i) => (
              <span key={a.id_annexe}>
                <strong>Annexe {i+1}</strong> : {a.nom_affichage}
                {i < annexes.length-1 ? '  ·  ' : ''}
              </span>
            ))}
          </div>
          <div style={{ marginTop: '4px', color: '#4ade80', fontSize: '10px' }}>
            Le devis PDF contiendra une page récapitulative de ces documents. Chaque annexe reste un fichier PDF indépendant.
          </div>
        </div>
      )}
    </div>
  );
}


